import { Component, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Location } from '@angular/common';
import { DatabaseService } from '../services/database.service';
import { UserService } from '../services/user.service';
import { ChangeService } from '../services/change.service';
import { AsaFormComponent } from '../asa-form/asa-form.component';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-view',
  standalone: false,
  templateUrl: './view.component.html',
  styleUrl: './view.component.scss'
})
export class ViewComponent {
  private readonly DUPLICATE_MEMBER_ISSUE = 'duplicate_member';
  private readonly SELF_MEMBER_ISSUE = 'self_member';
  private readonly UNKNOWN_GROUP_ISSUE = 'unknown_group';
  private readonly UNKNOWN_FORMAT_ISSUE = 'unknown_format';

  private issuesByObject = new Map<any, { severity: 'error' | 'warning'; code: string; message: string }>();

  private revalidateSelectedEditNode(): void {
    if (!this.selectedEditNode) return;
    if ((this.selectedEditNode as any).isRootDraft === true) return;
    try {
      this.validateMemberList((this.selectedEditNode as any).memberGroups, 'group');
    } catch { }
    try {
      this.validateMemberList((this.selectedEditNode as any).memberObjects, '');
    } catch { }
  }

  isInputError(obj: any): boolean {
    return this.issuesByObject.get(obj)?.severity === 'error';
  }

  isInputWarning(obj: any): boolean {
    return this.issuesByObject.get(obj)?.severity === 'warning';
  }

  getInputIssueMessage(obj: any): string {
    return this.issuesByObject.get(obj)?.message ?? '';
  }

  get hasBlockingEditErrors(): boolean {
    for (const issue of this.issuesByObject.values()) {
      if (issue.severity === 'error') return true;
    }
    return false;
  }

  private clearIssue(obj: any, code?: string): void {
    if (!obj) return;
    if (!code) {
      this.issuesByObject.delete(obj);
      return;
    }
    const existing = this.issuesByObject.get(obj);
    if (existing?.code === code) {
      this.issuesByObject.delete(obj);
    }
  }

  private setIssue(obj: any, severity: 'error' | 'warning', code: string, message: string): void {
    if (!obj) return;
    this.issuesByObject.set(obj, { severity, code, message });
  }

  private getBlockingIssuesSummary(): string {
    const messages = new Set<string>();
    for (const issue of this.issuesByObject.values()) {
      if (issue.severity === 'error' && issue.message) messages.add(issue.message);
    }
    return Array.from(messages).join('\n');
  }

  private validateMemberList(list?: any[], typeHint?: string): void {
    if (!Array.isArray(list)) return;
    console.log("Validating member list with type hint:", typeHint, list);

    // Recompute issues for current list items (prevents “sticky” errors).
    for (const item of list) {
      this.clearIssue(item);

      if (!item) continue;
      if (this.removedMembers?.has(item)) continue;

      const raw = (item?.memberName ?? item?.value ?? '').toString();
      const trimmed = raw.trim();
      if (!trimmed) continue;

      const typeRaw = (typeHint ?? item?.memberType ?? item?.type ?? '').toString().trim().toLowerCase();
      const isGroup = typeRaw === 'group';

      if (isGroup) {
        const currentGroupName = (this.selectedEditNode?.groupName ?? '').toString().trim();
        if (currentGroupName && this.normalizeGroupKey(trimmed) === this.normalizeGroupKey(currentGroupName)) {
          this.setIssue(item, 'error', this.SELF_MEMBER_ISSUE, 'Group cannot include itself');
          continue;
        }
        // Draft group name (top-level) can be new; required handling is done in the template.
        if (item && (item as any).isRootDraft === true) {
          continue;
        }

        // Group member rows must refer to an existing group.
        // Groups created in the current CR may not exist in the baseline tree yet.
        if (!this.isExistingGroupName(trimmed) && !this.isGroupCreatedInCurrentChange(trimmed)) {
          this.setIssue(item, 'error', this.UNKNOWN_GROUP_ISSUE, 'Group does not exist');
        }
        continue;
      }

      // Object rows: detect type; unknown is a warning (allowed for now).
      const detected = this.matchInputType(trimmed) || 'unknown';
      item.memberType = detected;
      if (detected === 'unknown') {
        this.setIssue(item, 'warning', this.UNKNOWN_FORMAT_ISSUE, 'Unknown format');
      }
    }

    // Validation (case-insensitive). Ignore removed rows.
    const seen = new Set<string>();
    for (const item of list) {
      console.log("Checking member for duplicates:", item);
      if (!item) continue;
      if (this.removedMembers?.has(item)) continue;
      const key = item.memberName;
      // why not just make key memberName

      if (!key) continue;

      if (seen.has(key)) {
        this.setIssue(item, 'error', this.DUPLICATE_MEMBER_ISSUE, 'Already a member');
      } else {
        console.log("Adding member to seen set:", key);
        seen.add(key);
      }
    }
  }

  private normalizeGroupKey(name: any): string {
    return (name ?? '').toString().trim().toLowerCase();
  }

  private normalizeMemberValue(value: any): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private rebuildCreatedGroupNames(changes: any[]): void {
    this.createdGroupNamesSet.clear();
    if (!Array.isArray(changes)) return;
    for (const group of changes) {
      const list = Array.isArray(group?.changes) ? group.changes : [];
      for (const change of list) {
        const s = (change?.summary ?? '').toString().trim();
        const match = s.match(/^Create\s+group:\s+(.+)$/i);
        if (!match) continue;
        const name = (match[1] ?? '').toString().trim();
        const key = this.normalizeGroupKey(name);
        if (key) this.createdGroupNamesSet.add(key);
      }
    }
  }

  private normalizeValueForChangeSummary(memberType: string, rawValue: string): string {
    const t = (memberType ?? '').toString().trim().toLowerCase();
    const v = (rawValue ?? '').toString().trim();
    if (!v) return v;

    // If WAF is inspecting request bodies, CIDR notation (e.g. 10.0.0.0/24) can sometimes
    // cause false-positive blocks. Convert CIDR to netmask form which we already accept.
    if (t === 'subnet') {
      return this.normalizeSubnetToNetmask(v);
    }

    return v;
  }

  private normalizeSubnetToNetmask(value: string): string {
    const v = (value ?? '').toString().trim();
    if (!v) return v;

    // If already in "ip netmask" form, normalize whitespace only.
    const parts = v.split(/\s+/).filter(Boolean);
    if (parts.length === 2 && this.looksLikeIpv4(parts[0]) && this.looksLikeIpv4(parts[1])) {
      return `${parts[0]} ${parts[1]}`;
    }

    // CIDR: "ip/prefix" (allow optional spaces around '/').
    const m = v.match(/^(\d{1,3}(?:\.\d{1,3}){3})\s*\/\s*(\d{1,2})$/);
    if (!m) return v;
    const ip = m[1];
    const prefix = Number.parseInt(m[2], 10);
    if (!Number.isFinite(prefix) || prefix < 0 || prefix > 32) return v;

    const mask = this.prefixToNetmask(prefix);
    return `${ip} ${mask}`;
  }

  private looksLikeIpv4(value: string): boolean {
    const s = (value ?? '').toString().trim();
    if (!s) return false;
    const parts = s.split('.');
    if (parts.length !== 4) return false;
    for (const p of parts) {
      if (!/^\d{1,3}$/.test(p)) return false;
      const n = Number.parseInt(p, 10);
      if (!Number.isFinite(n) || n < 0 || n > 255) return false;
    }
    return true;
  }

  private prefixToNetmask(prefix: number): string {
    // Build a 32-bit mask with the top `prefix` bits set.
    // prefix=0 => 0.0.0.0
    // prefix=32 => 255.255.255.255
    const clamped = Math.max(0, Math.min(32, Math.trunc(prefix)));
    const bytes: number[] = [];

    let remaining = clamped;
    for (let i = 0; i < 4; i += 1) {
      const bits = Math.max(0, Math.min(8, remaining));
      remaining -= bits;
      // 8 bits => 255, 0 bits => 0, otherwise leftmost bits set.
      const byte = bits === 0 ? 0 : (0xff << (8 - bits)) & 0xff;
      bytes.push(byte);
    }

    return bytes.join('.');
  }

  private getMemberDedupeKey(member: any, typeHint?: string): string | null {
    if (!member) return null;
    const rawValue = (member?.memberName ?? member?.value ?? '').toString();
    const trimmed = rawValue.trim();
    if (!trimmed) return null;

    const typeRaw = (typeHint ?? member?.memberType ?? member?.type ?? '').toString().trim().toLowerCase();
    if (typeRaw === 'group') {
      return `group::${this.normalizeGroupKey(trimmed)}`;
    }

    // Non-group members: treat duplicates as same value regardless of detected type.
    return `value::${this.normalizeMemberValue(trimmed)}`;
  }

  private dedupeMemberList(list?: any[], typeHint?: string): void {
    // NOTE: despite the historical name, we do NOT delete duplicates anymore.
    // We keep the user’s text and mark duplicates as blocking errors.
    this.validateMemberList(list, typeHint);
  }

  private isExistingGroupName(name: any): boolean {
    const key = this.normalizeGroupKey(name);
    if (!key) return false;

    if (this.allGroupNamesSet.has(key)) return true;

    // Fallback to tree lookups (defensive for timing/order-of-load edge cases).
    try {
      if (Array.isArray(this.selectedTree) && this.findGroupInTree(this.selectedTree, name)) return true;
    } catch {}
    try {
      if (this.originalTree && this.findGroupInTree(this.originalTree, name)) return true;
    } catch {}

    return false;
  }

  private isGroupCreatedInCurrentChange(name: any): boolean {
    const key = this.normalizeGroupKey(name);
    if (!key) return false;
    return this.createdGroupNamesSet.has(key);
  }

  hasPendingChanges(groupName: any): boolean {
    const key = this.normalizeGroupKey(groupName);
    if (!key) return false;
    const groups = Array.isArray(this.allFilteredChanges) ? this.allFilteredChanges : [];
    const g = groups.find((x: any) => this.normalizeGroupKey(x?.groupName) === key);
    return Array.isArray(g?.changes) && g.changes.length > 0;
  }

  private getDeletedGroupKeys(): Set<string> {
    // Keys to hide from the left tree.
    // Only groups explicitly deleted should be hidden.
    const out = new Set<string>();
    const groups = Array.isArray(this.allFilteredChanges) ? this.allFilteredChanges : [];
    for (const g of groups) {
      const parentKey = this.normalizeGroupKey(g?.groupName);
      if (!parentKey) continue;
      const changes = Array.isArray(g?.changes) ? g.changes : [];
      const hasDelete = changes.some((c: any) => ((c?.summary || '').toString().trim().toLowerCase()).startsWith('delete group:'));
      if (!hasDelete) continue;

      // Hide the deleted group itself.
      out.add(parentKey);
    }
    return out;
  }

  private getCascadeRemovedSubgroupNamesForDeletedGroup(groupName: string): string[] {
    const name = (groupName || '').toString().trim();
    if (!name) return [];
    const key = this.normalizeGroupKey(name);
    const current = this._user.getFilteredChanges?.() ?? [];
    if (!key || !Array.isArray(current)) return [];

    const entry = current.find((g: any) => this.normalizeGroupKey(g?.groupName) === key);
    const changes = Array.isArray(entry?.changes) ? entry.changes : [];
    const hasDelete = changes.some((c: any) => ((c?.summary || '').toString().trim().toLowerCase()).startsWith('delete group:'));
    if (!hasDelete) return [];

    const out: string[] = [];
    const seen = new Set<string>();
    for (const c of changes) {
      const s = (c?.summary || '').toString().trim();
      const m = s.match(/^Remove\s+group:\s+(.+)$/i);
      if (!m) continue;
      const childName = (m[1] || '').toString().trim();
      const childKey = this.normalizeGroupKey(childName);
      if (!childKey || seen.has(childKey)) continue;
      seen.add(childKey);
      out.push(childName);
    }
    return out;
  }

  private applyDeletedGroupsVisibility(): void {
    const deleted = this.getDeletedGroupKeys();
    if (deleted.size === 0) {
      return;
    }

    const removeKeysFrom = (nodes: any[] | null | undefined): any[] => {
      if (!Array.isArray(nodes)) return [];
      const out: any[] = [];
      for (const n of nodes) {
        const k = this.normalizeGroupKey(n?.groupName);
        if (k && deleted.has(k)) {
          this.expandedNodes.delete(n.groupName);
          continue;
        }
        if (Array.isArray(n?.members) && n.members.length) {
          n.members = removeKeysFrom(n.members);
        }
        out.push(n);
      }
      return out;
    };

    if (Array.isArray(this.selectedTree)) {
      this.selectedTree = removeKeysFrom(this.selectedTree);
    }
    if (Array.isArray(this.searchResults)) {
      this.searchResults = this.searchResults.filter((n: any) => {
        const k = this.normalizeGroupKey(n?.groupName);
        return !(k && deleted.has(k));
      });
    }

    if (this.selectedEditNode) {
      const editingKey = this.normalizeGroupKey(this.selectedEditNode.groupName || this.selectedEditNode.value || '');
      if (editingKey && deleted.has(editingKey)) {
        this.unselectEditNode();
      }
    }
  }

  private restoreGroupFromBaseline(groupName: string): void {
    const name = (groupName || '').toString().trim();
    if (!name) return;
    const existing = this.findGroupInTree(Array.isArray(this.selectedTree) ? this.selectedTree : [], name);
    if (existing) return;

    const baseline = this.originalTree ? this.findGroupInTree(this.originalTree, name) : null;
    if (baseline) {
      const clone = JSON.parse(JSON.stringify(baseline));
      if (!Array.isArray(this.selectedTree)) this.selectedTree = [];
      if (!Array.isArray(this.searchResults)) this.searchResults = [];
      this.selectedTree.unshift(clone);
      this.searchResults.unshift(clone);
      this.rebuildGroupSuggestionsIndex();
      this.rebuildObjectSuggestionsIndex();
      return;
    }

    // Fallback: create an empty placeholder.
    this.ensureGroupInTree(name);
  }

  private buildRecursiveDeleteGroupChanges(rootGroupName: string): Array<{ summary: string; meta?: string }> {
    const rootName = (rootGroupName || '').toString().trim();
    if (!rootName) return [];
    // Keep behavior minimal: deleting a group only stages the group deletion itself.
    return [{ summary: `Delete group: ${rootName}` }];
  }

  getPendingAddedMembers(
    groupName: any,
    existingMembers?: Array<{ memberType?: string; memberName?: string }>
  ): Array<{ memberType: string; memberName: string }> {
    const key = this.normalizeGroupKey(groupName);
    if (!key) return [];

    // When a group is newly created in the UI, its staged members can exist both:
    // 1) In the tree node's `members` array (for immediate display)
    // 2) As pending "Add ..." changes in View Changes
    // Filter out anything already present in the tree to avoid visual duplicates.
    const alreadyInTree = new Set<string>();
    const baselineMembers = Array.isArray(existingMembers) ? existingMembers : [];
    for (const m of baselineMembers) {
      const name = (m?.memberName ?? '').toString().trim();
      if (!name) continue;
      const type = (m?.memberType ?? '').toString().trim().toLowerCase();
      alreadyInTree.add(`${type}::${name.toLowerCase()}`);
    }

    const groups = Array.isArray(this.allFilteredChanges) ? this.allFilteredChanges : [];
    const g = groups.find((x: any) => this.normalizeGroupKey(x?.groupName) === key);
    const changes = Array.isArray(g?.changes) ? g!.changes : [];

    const seen = new Set<string>();
    const out: Array<{ memberType: string; memberName: string }> = [];
    for (const c of changes) {
      const s = (c?.summary || '').toString().trim();
      const m = s.match(/^(Add)\s+(\w+):\s+(.+)$/i);
      if (!m) continue;
      const memberType = (m[2] || '').toString().trim();
      const memberName = (m[3] || '').toString().trim();
      if (!memberName) continue;
      const dedupeKey = `${memberType.toLowerCase()}::${memberName.toLowerCase()}`;
      if (alreadyInTree.has(dedupeKey)) continue;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push({ memberType, memberName });
    }
    return out;
  }

  isGroupNameEditDisabled(obj: any): boolean {
    if (this.isEditFlowBlocked) {
      return true;
    }
    // Renaming existing top-level groups is represented as delete+create, which is confusing in the UI.
    // Allow editing only for the "Create New Group" draft.
    return !(obj && (obj as any).isRootDraft === true);
  }

  private readonly cardKeys = ['inventory', 'interface', 'dns'] as const;
  private readonly defaultCardState = {
    inventory: true,
    interface: true,
    dns: true
  } as const;

  allInventory: any;
  selectedInventory: any;
  selectedInterface: any;
  selectedDns: any;
  selectedTree: any;
  private originalTree: any;
  loadingTree: boolean = false;
  showMoreInfo = false;
  searchTerm: string = '';
  deviceSearchTerm: string = '';
  searchResults: any[] = [];
  // Flat list of all group names from the loaded tree for suggestions
  allGroupNames: string[] = [];
  private allGroupNamesLower: string[] = [];
  private allGroupNamesSet = new Set<string>();
  // Flat list of known object values (fqdn/host/subnet/range) from the loaded tree for suggestions
  allObjectValues: string[] = [];
  private allObjectValuesLower: string[] = [];
  private readonly suggestionCacheLimit = 200;
  private groupSuggestionCache = new Map<string, string[]>();
  private objectSuggestionCache = new Map<string, string[]>();
  private createdGroupNamesSet = new Set<string>();
  private readonly inputDebounceMs = 1000;
  private inputDebounceTimers = new Map<any, ReturnType<typeof setTimeout>>();
  // Track which input objects are focused to control suggestion visibility
  private focusedInputs = new Set<any>();

  expandedNodes = new Set<string>();
  showAllMembers = new Set<string>();
  readonly INITIAL_MEMBER_LIMIT = 5; // Show first 5 members by default
  selectedAsms: any = null;
  selectedAsmsEntries: any[] = [];
  inventoryDialogOpen = false;
  isSettingGoldSource: boolean = false;

  pendingChanges: Array<{ summary: string; meta?: string }> = []; // Used for quick comparisons and checking, not displayed
  // Track changes per group and original snapshots per group
  private pendingChangesByGroup = new Map<string, Array<{ summary: string; meta?: string }>>();
  private originalSnapshotsByGroup = new Map<string, any>();
  private activeGroupKey: string | null = null;
  // Persist the current edited state per group so returning to a group restores inputs
  private editedNodesByGroup = new Map<string, any>();

  // Map to track group name to index for observable storage
  private groupIndexMap = new Map<string, number>();
  private nextGroupIndex = 0;
  // All changes from all groups (subscribed from observable)
  allFilteredChanges: Array<{ groupName: string; changes: Array<{ summary: string; meta?: string }> }> = [];

  // Currently selected node for side-panel editing (used only in Add Objects/Groups flow)
  selectedEditNode: any | null = null;
  private selectedEditNodeIndex: number | null = null;
  private originalEditNodeSnapshot: any | null = null;
  // Node currently highlighted in the tree (for blue background only)
  activeNode: any | null = null;
  // Draft text for adding new members inline per node id/reference
  private inlineDrafts = new Map<any, { value: string; type: string }>();
  // Track nodes created during the current edit session
  private newlyAddedNodes = new Set<any>();
  // Input validation is tracked via issuesByObject (errors + warnings)
  // Track members marked for removal (but still displayed with Remove tag)
  private removedMembers = new Set<any>();

  // Collapse functionality
  isCollapsed = false;
  cardStates: Record<typeof this.cardKeys[number], boolean> = { ...this.defaultCardState };
  inventoryCollapsed = true;
  user: any;
  currentCrNumber: any;
  currentChange: any = null;
  activeChanges: any[] = [];
  showNewCrInput: boolean = false;
  newCrNumber: string = '';
  newCrNumberError: string = '';
  changeSelectionError: string = '';
  isSavingChanges: boolean = false;
  treeEditsSaved: boolean = false;

  // View Changes pane loading state (specifically for CR switching).
  viewChangesLoading: boolean = false;
  private viewChangesLoadSeq = 0;
  private lastSelectedCrNumber: string | null = null;

  private shouldAutoOpenTreeEdit = false;
  private hasAutoOpenedTreeEdit = false;
  private pendingUrlCrNumber: string | null = null;

  // Check if the entered CR number already exists
  get isCrNumberDuplicate(): boolean {
    if (!this.newCrNumber.trim() || !this.activeChanges) {
      return false;
    }
    return this.activeChanges.some(change => 
      change.crNumber && change.crNumber.toLowerCase() === this.newCrNumber.trim().toLowerCase()
    );
  }

  // Check if save button should be disabled
  get isSaveDisabled(): boolean {
    const needsCrNumber = !this.currentCrNumber && this.showNewCrInput && !this.newCrNumber.trim();
    return this.isSavingChanges || this.isCrNumberDuplicate || needsCrNumber || this.hasBlockingEditErrors;
  }

  get hasSelectedOrCreatingChange(): boolean {
    const cr = (this.currentCrNumber ?? '').toString().trim();
    const hasExisting = !!cr && cr !== 'CREATE_NEW';

    // For "Create New CR" flow, require a valid new Change ID before enabling edits.
    const hasNewId = this.showNewCrInput && !!this.newCrNumber.trim();
    const canCreateNew = hasNewId && !this.isCrNumberDuplicate && !this.isUserMissingForNewChange;

    return hasExisting || canCreateNew;
  }

  get isEditFlowBlocked(): boolean {
    return !this.hasSelectedOrCreatingChange;
  }

  private promptForChangeSelection(actionLabel?: string): void {
    const action = (actionLabel || '').toString().trim();
    this.changeSelectionError = action
      ? `Select an existing change request, or choose “+ Create New CR” and enter a new Change ID to ${action}.`
      : 'Select an existing change request, or choose “+ Create New CR” and enter a new Change ID to start editing.';

    // Bring the Change Control selector into view and focus it.
    setTimeout(() => {
      try {
        const el = document.getElementById('changeRequestSelect') as HTMLSelectElement | null;
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus();
      } catch { }
    }, 0);
  }

  private requireChangeSelection(actionLabel?: string): boolean {
    if (!this.isEditFlowBlocked) return true;
    this.promptForChangeSelection(actionLabel);
    return false;
  }

  private hasAnyPendingEdits(): boolean {
    try {
      const groups = this._user.getFilteredChanges?.() ?? [];
      const hasFiltered = Array.isArray(groups) && groups.some((g: any) => (g?.groupName || '').toString().trim() && Array.isArray(g?.changes) && g.changes.length > 0);
      return (
        hasFiltered ||
        this.pendingChangesByGroup.size > 0 ||
        this.editedNodesByGroup.size > 0 ||
        this.originalSnapshotsByGroup.size > 0 ||
        this.removedMembers.size > 0 ||
        this.newlyAddedNodes.size > 0
      );
    } catch {
      return (
        this.pendingChangesByGroup.size > 0 ||
        this.editedNodesByGroup.size > 0 ||
        this.originalSnapshotsByGroup.size > 0 ||
        this.removedMembers.size > 0 ||
        this.newlyAddedNodes.size > 0
      );
    }
  }

  private resetAllChangeControlEdits(): void {
    // Clear all pending changes in the View (Change Control selection is the master).
    this._user.clearFilteredChanges();
    this.pendingChanges = [];
    this.allFilteredChanges = [];
    this.pendingChangesByGroup.clear();
    this.originalSnapshotsByGroup.clear();
    this.editedNodesByGroup.clear();
    this.activeGroupKey = null;

    // Reset per-group indexing used by filteredChanges$ updates.
    this.groupIndexMap.clear();
    this.nextGroupIndex = 0;

    // Reset editor/UI state.
    this.selectedEditNode = null;
    this.selectedEditNodeIndex = null;
    this.originalEditNodeSnapshot = null;
    this.activeNode = null;
    this.inlineDrafts.clear();
    this.newlyAddedNodes.clear();
    this.issuesByObject.clear();
    this.removedMembers.clear();
    this.focusedInputs.clear();
    this.newCrNumberError = '';

    // Discard any unsaved tree edits by restoring from the last loaded baseline.
    if (this.originalTree) {
      this.selectedTree = JSON.parse(JSON.stringify(this.originalTree));
      this.rebuildGroupSuggestionsIndex();
      this.rebuildObjectSuggestionsIndex();
      this.searchResults = Array.isArray(this.selectedTree) ? [...this.selectedTree] : [];
    }

    // Clear expand/show state to avoid showing stale diffs.
    this.expandedNodes.clear();
    this.showAllMembers.clear();
  }

  private getCurrentUserGmid(): string | null {
    const gmid = (this.user?.gmid ?? '').toString().trim();
    return gmid ? gmid : null;
  }

  get isUserMissingForNewChange(): boolean {
    return this.showNewCrInput && !this.getCurrentUserGmid();
  }

  constructor(
    private _db: DatabaseService,
    private _user: UserService,
    private _change: ChangeService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) { }

  get filteredInventory() {
    if (!this.allInventory) return [];
    const sortDevices = (arr: any[]) => [...arr].sort((a, b) => {
      // Prioritize Gold Source (masterConfig === 1) first
      const ag = a && a.masterConfig === 1 ? 1 : 0;
      const bg = b && b.masterConfig === 1 ? 1 : 0;
      if (ag !== bg) return bg - ag; // gold (1) comes first
      const ah = (a.hostname || '').toLowerCase();
      const bh = (b.hostname || '').toLowerCase();
      if (ah < bh) return -1;
      if (ah > bh) return 1;
      return 0;
    });
    if (!this.deviceSearchTerm || this.deviceSearchTerm.trim() === '') {
      return sortDevices(this.allInventory);
    }
    const term = this.deviceSearchTerm.toLowerCase();
    const filtered = this.allInventory.filter((item: any) => {
      return (
        (item.hostname && item.hostname.toLowerCase().includes(term)) ||
        (item.hpnaGroup && item.hpnaGroup.toLowerCase().includes(term)) ||
        (item.serialNumber && item.serialNumber.toLowerCase().includes(term)) ||
        (item.mgmtIpAddress && item.mgmtIpAddress.toLowerCase().includes(term))
      );
    });
    return sortDevices(filtered);
  }

  ngOnInit() {
    // Route entry: clear any leftover staged changes from a previous session/CR.
    // UserService is a singleton, so its filteredChanges$ can persist across page navigations.
    this.resetAllChangeControlEdits();
    this.currentCrNumber = null;
    this.currentChange = null;
    this.showNewCrInput = false;
    this.newCrNumber = '';
    this.pendingUrlCrNumber = null;
    this.shouldAutoOpenTreeEdit = false;
    this.hasAutoOpenedTreeEdit = false;

    // Capture URL-driven intent early so async loads (activeChanges/tree) can react to it.
    this.route.queryParams.subscribe(params => {
      const editModeParam = params['editMode'];
      this.shouldAutoOpenTreeEdit = editModeParam === true || editModeParam === 'true' || editModeParam === '1';
      this.pendingUrlCrNumber = (params['crNumber'] ?? null) as any;
      if (this.pendingUrlCrNumber) {
        this.currentCrNumber = this.pendingUrlCrNumber;
      }

      // If active changes are already loaded, apply selection immediately.
      this.applyCrSelectionFromUrl();
    });

    // Subscribe to current user
    this._user.user$.subscribe(user => {
      this.user = user;
    });

    // Load active changes for dropdown
    this.refreshActiveChanges();

    // Subscribe to filtered changes observable (single source of truth for View Changes)
    this._user.filteredChanges$.subscribe(changes => {
      // Any change emission implies the staged edits have changed; clear the saved indicator.
      // (Save itself does not emit; only subsequent edits should clear it.)
      if (this.treeEditsSaved && !this.isSavingChanges) {
        this.treeEditsSaved = false;
      }

      // Keep per-group indices aligned with the latest filteredChanges array.
      // Without this, remove/edit flows can publish to the wrong index and the View Changes pane
      // can appear stale or inconsistent after loading a CR.
      this.syncGroupIndexMapFromUserFilteredChanges();

      this.allFilteredChanges = (changes || [])
        .map(group => ({
          ...group,
          changes: this.sortChanges(group.changes)
        }))
        .filter(group => {
          const name = (group?.groupName ?? '').toString().trim();
          return name.length > 0 && Array.isArray(group?.changes) && group.changes.length > 0;
        });
      
      // Ensure all groups from loaded changes exist in the tree
      this.ensureGroupsExistInTree(this.allFilteredChanges);

      // Hide groups that are staged for deletion in the selected CR.
      this.applyDeletedGroupsVisibility();
      
      // Flatten for the View Changes pane display; do not mutate elsewhere
      this.pendingChanges = this.allFilteredChanges.flatMap(group =>
        group.changes.map(change => ({ ...change, groupName: group.groupName }))
      );

      this.rebuildCreatedGroupNames(changes || []);

      // If navigated here from Change Control edit action, jump straight into edit view
      // once pending changes and the tree are available.
      this.tryAutoOpenTreeEditView();
    });

    this._db.getAllInventory().subscribe((res) => {
      this.allInventory = res;
      // Auto-select based on query params or default to Gold Source
      if (!this.selectedInventory && Array.isArray(this.allInventory)) {
        // Check if serialNumber is provided in query params (from compare component fix action)
        this.route.queryParams.subscribe(params => {
          const requestedSerialNumber = params['serialNumber'];
          let itemToSelect = null;

          if (requestedSerialNumber) {
            // Select the inventory item matching the requested serialNumber
            itemToSelect = this.allInventory.find((item: any) => item.serialNumber === requestedSerialNumber);
          }

          if (!itemToSelect) {
            // Default to Gold Source (masterConfig === 1)
            itemToSelect = this.allInventory.find((item: any) => item && item.masterConfig === 1);
          }

          if (itemToSelect) {
            this.selectItem(itemToSelect);
            // Capture CR number from query params for direct change edits.
            // Do not overwrite an already-selected CR with null after we consume deep-link params.
            const urlCr = (params['crNumber'] ?? '').toString().trim();
            if (urlCr) {
              this.currentCrNumber = urlCr;
            }
            // Apply after active changes are available (avoids selecting before dropdown data loads)
            this.applyCrSelectionFromUrl();
          }
        });
      }
    });
  }

  private applyCrSelectionFromUrl() {
    if (!this.pendingUrlCrNumber) {
      return;
    }
    if (!Array.isArray(this.activeChanges) || this.activeChanges.length === 0) {
      return;
    }
    const cr = this.pendingUrlCrNumber;
    this.pendingUrlCrNumber = null;
    this.currentCrNumber = cr;
    this.onChangeSelected(cr, { suppressConfirm: true });

    // Consume deep-link params so a subsequent browser refresh doesn't automatically
    // re-select the same CR and rehydrate its saved cliConfig back into the UI.
    try {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { crNumber: null, editMode: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    } catch { }
  }

  private tryAutoOpenTreeEditView() {
    if (!this.shouldAutoOpenTreeEdit || this.hasAutoOpenedTreeEdit) {
      return;
    }
    if (!Array.isArray(this.searchResults) || this.searchResults.length === 0) {
      return;
    }

    const pendingGroupName = (this.allFilteredChanges || []).find(g =>
      (g?.groupName ?? '').toString().trim() && Array.isArray(g?.changes) && g.changes.length > 0
    )?.groupName;

    // Ensure the target group is visible in the list (clear any filtering search).
    if ((this.searchTerm || '').trim().length >= 2) {
      this.searchTerm = '';
      this.search();
    }

    const pendingTrimmed = (pendingGroupName ?? '').toString().trim();
    const pendingLower = pendingTrimmed.toLowerCase();

    let targetGroup: any | null = null;
    if (pendingLower) {
      targetGroup = this.searchResults.find((g: any) =>
        (g?.groupName ?? '').toString().trim().toLowerCase() === pendingLower
      ) ?? null;

      // If pending group isn't in the loaded tree (yet), create it so we can open edit view.
      if (!targetGroup && Array.isArray(this.allFilteredChanges) && this.allFilteredChanges.length) {
        this.ensureGroupsExistInTree(this.allFilteredChanges);
        targetGroup = this.searchResults.find((g: any) =>
          (g?.groupName ?? '').toString().trim().toLowerCase() === pendingLower
        ) ?? null;
      }
    }

    if (!targetGroup) {
      targetGroup = this.searchResults[0] ?? null;
    }

    const targetGroupName = (targetGroup?.groupName ?? targetGroup?.value ?? '').toString().trim();
    if (!targetGroupName) {
      return;
    }

    this.isCollapsed = false;
    this.expandedNodes.add(targetGroupName);
    this.selectEditNode(targetGroup);
    setTimeout(() => {
      const el = document.getElementById(`group-${targetGroupName}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    this.hasAutoOpenedTreeEdit = true;
  }

  onInventoryChange(selected: any) {
    if (selected && selected.serialNumber) {
      this.loadingTree = true;
      this.selectedInterface = null;
      this.selectedDns = null;
      this.selectedTree = null;
      this.searchResults = [];

      this._db.getInterfaceBySerial(selected.serialNumber).subscribe((res) => {
        this.selectedInterface = res;
        if (!res || !res.length) {
          this.setCardCollapsed('interface', true);
        }
      });

      this._db.getDnsBySerial(selected.serialNumber).subscribe((res) => {
        this.selectedDns = res;
        if (!res || !res.length) {
          this.setCardCollapsed('dns', true);
        }
      });

      this._db.getTreeBySerial(selected.serialNumber).subscribe((res) => {
        this.originalTree = this.sortTree(res || []);
        // Work on a mutable copy for edits
        this.selectedTree = JSON.parse(JSON.stringify(this.originalTree));
        this.loadingTree = false;
        // Build group name suggestions list
        this.rebuildGroupSuggestionsIndex();
        this.rebuildObjectSuggestionsIndex();
        this.search();

        // Tree load can overwrite placeholder groups added earlier; re-ensure pending-change groups exist.
        if (Array.isArray(this.allFilteredChanges) && this.allFilteredChanges.length) {
          this.ensureGroupsExistInTree(this.allFilteredChanges);
          this.applyDeletedGroupsVisibility();
        }

        // If navigated here from Change Control edit action, jump straight into edit view.
        this.tryAutoOpenTreeEditView();

        // Tree/group-name index is now authoritative; clear any stale validation errors.
        this.revalidateSelectedEditNode();
      });
      // this._db.getGroupRootBySerial(selected.serialNumber).subscribe((res) => {
      // });
    } else {
      this.selectedInterface = null;
      this.selectedDns = null;
      this.setCardCollapsed('inventory', true);
      this.setCardCollapsed('interface', true);
      this.setCardCollapsed('dns', true);
    }
  }

  //filter for search - triggered by search button (shows all results)
  search() {
    if (this.loadingTree) return;
    this.expandedNodes.clear();
    if (!this.selectedTree) {
      this.searchResults = [];
      return;
    }
    const trimmed = (this.searchTerm || '').trim();
    // If empty or fewer than 2 chars, show all roots collapsed
    if (trimmed.length < 2) {
      this.searchResults = [...this.selectedTree];
      return;
    }
    const term = trimmed.toLowerCase();
    // Search through groupName and memberName fields
    this.searchResults = this.selectedTree
      .map((root: any) => {
        // Check if groupName matches
        const groupNameMatches = (root.groupName || '').toLowerCase().includes(term);
        
        // Filter members array to only include matching members
        const filteredMembers = root.members ? root.members.filter((member: any) => 
          (member.memberName || '').toLowerCase().includes(term)
        ) : [];
        
        // If groupName matches, return entire group
        if (groupNameMatches) {
          // Auto-expand if there are members
          if (root.members && root.members.length > 0) {
            this.expandedNodes.add(root.groupName);
          }
          return root;
        }
        
        // If members match, return group with original members preserved
        if (filteredMembers.length > 0) {
          // Auto-expand groups with matching members
          this.expandedNodes.add(root.groupName);
          return { ...root, originalMembers: root.members };
        }
        
        return null;
      })
      .filter((root: any) => root !== null)
      .sort((a: any, b: any) => {
        const ag = (a.groupName || '').toLowerCase();
        const bg = (b.groupName || '').toLowerCase();
        if (ag < bg) return -1;
        if (ag > bg) return 1;
        return 0;
      });
  }

  // Live search as user types (show all matching results)
  liveSearch() {
    if (this.loadingTree) return;
    this.expandedNodes.clear();
    if (!this.selectedTree) {
      this.searchResults = [];
      return;
    }
    const trimmed = (this.searchTerm || '').trim();
    if (trimmed.length < 2) {
      this.searchResults = [...this.selectedTree];
      return;
    }
    const term = trimmed.toLowerCase();
    this.searchResults = this.selectedTree
      .map((root: any) => {
        // Check if groupName matches
        const groupNameMatches = (root.groupName || '').toLowerCase().includes(term);
        
        // Filter members array to only include matching members
        const filteredMembers = root.members ? root.members.filter((member: any) => 
          (member.memberName || '').toLowerCase().includes(term)
        ) : [];
        
        // If groupName matches, return entire group
        if (groupNameMatches) {
          // Auto-expand if there are members
          if (root.members && root.members.length > 0) {
            this.expandedNodes.add(root.groupName);
          }
          return root;
        }
        
        // If members match, return group with original members preserved
        if (filteredMembers.length > 0) {
          // Auto-expand groups with matching members
          this.expandedNodes.add(root.groupName);
          return { ...root, originalMembers: root.members };
        }
        
        return null;
      })
      .filter((root: any) => root !== null)
      .sort((a: any, b: any) => {
        const ag = (a.groupName || '').toLowerCase();
        const bg = (b.groupName || '').toLowerCase();
        if (ag < bg) return -1;
        if (ag > bg) return 1;
        return 0;
      });
  }

  isCardCollapsed(key: typeof this.cardKeys[number]): boolean {
    return this.cardStates[key];
  }

  toggleCard(key: typeof this.cardKeys[number]) {
    this.cardStates[key] = !this.cardStates[key];
  }

  toggleInventorySection() {
    this.inventoryCollapsed = !this.inventoryCollapsed;
  }

  private setCardCollapsed(key: typeof this.cardKeys[number], collapsed: boolean) {
    this.cardStates[key] = collapsed;
  }

  private resetCardStates(collapsed: boolean = true) {
    this.cardStates = this.cardKeys.reduce((state, key) => {
      state[key] = collapsed;
      return state;
    }, {} as Record<typeof this.cardKeys[number], boolean>);
  }

  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  getDisplayEntries(source: any, excluded: string[] = []): Array<{ key: string; value: any }> {
    if (!source) {
      return [];
    }

    return Object.entries(source)
      .filter(([key]) => !excluded.includes(key))
      .filter(([, value]) => this.hasRenderableValue(value))
      .map(([key, value]) => ({ key, value }));
  }

  private hasRenderableValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }

    return true;
  }

  // Method to truncate version to two decimal points
  getTruncatedVersion(version: string): string {
    if (!version) return '';
    // Extract version number from string like "9.20(4)10" -> "9.20"
    const match = version.match(/^(\d+\.\d+)/);
    return match ? `v${match[1]}` : `v${version}`;
  }

  // Method to format lastModified date string to readable format
  formatLastModified(dateString: string): string {
    if (!dateString) return '';
    
    try {
      // Parse the date string format: "23:00:44.673 UTC Wed Oct 8 2025"
      // Extract the date part and convert to readable format
      const dateRegex = /(\w{3})\s+(\w{3})\s+(\d{1,2})\s+(\d{4})/;
      const match = dateString.match(dateRegex);
      
      if (match) {
        const [, , monthAbbr, day, year] = match;
        return `${monthAbbr} ${day}, ${year}`;
      }
      
      // Fallback: try to parse as a regular date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      return dateString; // Return original if parsing fails
    } catch (error) {
      return dateString; // Return original if any error occurs
    }
  }

  formatMetaLabel(key: string): string {
    if (!key) {
      return '';
    }

    const spaced = key
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2');

    return spaced
      .split(' ')
      .filter(part => part.trim().length)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  // Dropdown state
  dropdownOpen = false;

  // Toggle dropdown open/close
  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  // Select item from dropdown
  selectItem(item: any) {
    this.selectedInventory = item;
    this.dropdownOpen = false;
    this.showMoreInfo = false;
    this.resetCardStates();
    this.setCardCollapsed('inventory', false);
    this.onInventoryChange(item);
  }

  // Toggle collapse/expand state
  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.custom-dropdown');
    if (!dropdown && this.dropdownOpen) {
      this.dropdownOpen = false;
    }
  }

  toggleNode(node: any) {
    if (this.expandedNodes.has(node.groupName)) {
      this.expandedNodes.delete(node.groupName);
    } else {
      this.expandedNodes.add(node.groupName);
    }
  }

  isExpanded(node: any): boolean {
    return this.expandedNodes.has(node.groupName);
  }

  jumpToGroup(groupName: string, currentGroupName: string) {
    // Find the group in searchResults
    const targetKey = this.normalizeGroupKey(groupName);
    const targetGroup = this.searchResults.find((result: any) =>
      this.normalizeGroupKey(result?.groupName) === targetKey
    );
    
    if (targetGroup) {
      // Collapse the current group
      this.expandedNodes.delete(currentGroupName);
      
      // Expand the target group
      this.expandedNodes.add(targetGroup.groupName);
      
      // Scroll to the group
      setTimeout(() => {
        const elements = document.querySelectorAll('.group-name');
        for (const el of Array.from(elements)) {
          if (this.normalizeGroupKey(el.textContent?.trim()) === targetKey) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            break;
          }
        }
      }, 100);
    }
  }

  navigateToGroup(groupName: string) {
    const targetKey = this.normalizeGroupKey(groupName);
    
    // Find the group node in searchResults or selectedTree
    let targetNode = this.searchResults.find((result: any) =>
      this.normalizeGroupKey(result?.groupName) === targetKey
    );
    
    // If not found in searchResults, search in selectedTree recursively
    if (!targetNode && this.selectedTree) {
      const findNode = (nodes: any[]): any => {
        for (const node of nodes) {
          if (this.normalizeGroupKey(node.groupName) === targetKey) return node;
          if (node.members && node.members.length) {
            const found = findNode(node.members);
            if (found) return found;
          }
        }
        return null;
      };
      targetNode = findNode(this.selectedTree);
    }
    
    if (targetNode) {
      // Expand the node first
      this.expandedNodes.add(targetNode.groupName);
      
      // Use the existing selectEditNode functionality to set up the edit mode
      this.selectEditNode(targetNode);
      
      // Scroll to the group using its ID
      setTimeout(() => {
        const elementId = `group-${targetNode.groupName}`;
        const targetElement = document.getElementById(elementId);
        
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);
    }
  }

  /**
   * Remove a change from the summary panel
   * If it's an "Add" change, remove the member from the edit arrays
   * If it's a "Remove" change, restore the member by removing from removedMembers set
   */
  removeChangeFromSummary(groupName: string, change: { summary: string; meta?: string }) {
    const summaryText = (change?.summary || '').toString().trim();
    const canonicalGroupName = (groupName || '').toString().trim();
    const effectiveGroupName = canonicalGroupName || (change as any)?.groupName || groupName;
    
    // Parse the change summary to determine action and details
    // Format: "Add type: name" or "Remove type: name"
    const match = summaryText.match(/^(Add|Remove)\s+(\w+):\s+(.+)$/);
    if (match) {
      const [, action, memberType, memberName] = match;
      const normalizedType = (memberType || '').toLowerCase();

      // Remove the line from the View Changes source-of-truth immediately.
      // This avoids timing/index issues where navigation/edit mode isn't ready yet.
      this.removeSummaryLineFromUserFilteredChanges(effectiveGroupName, summaryText);

      // Navigate to the group if not already editing it
      if (!this.selectedEditNode || this.normalizeGroupKey(this.selectedEditNode.groupName) !== this.normalizeGroupKey(groupName)) {
        this.navigateToGroup(groupName);

        // Wait for navigation to complete
        setTimeout(() => {
          this.processChangeRemoval(action, normalizedType, memberName);
        }, 300);
      } else {
        this.processChangeRemoval(action, normalizedType, memberName);
      }
      return;
    }

    // Group-level change lines
    const deleteMatch = summaryText.match(/^Delete\s+group:\s+(.+)$/i);
    if (deleteMatch) {
      const nameFromSummary = (deleteMatch[1] || '').toString().trim();
      const name = canonicalGroupName || nameFromSummary;
      this.undoDeleteGroupChange(name);
      return;
    }

    const createMatch = summaryText.match(/^Create\s+group:\s+(.+)$/i);
    if (createMatch) {
      const nameFromSummary = (createMatch[1] || '').toString().trim();
      const name = canonicalGroupName || nameFromSummary;
      this.undoCreateGroupChange(name);
      return;
    }

    const renameMatch = summaryText.match(/^Rename\s+subgroup:\s+(.+)\s+→\s+(.+)$/i);
    if (renameMatch) {
      // Best-effort: remove this single rename line from the group's staged changes.
      this.removeSummaryLineFromUserFilteredChanges(effectiveGroupName, summaryText);
      return;
    }

    console.error('Could not parse change summary:', summaryText);
  }

  private getOrCreateGroupIndex(groupName: string): number {
    const key = this.normalizeGroupKey(groupName);
    if (key) {
      const existingIdx = this.getGroupIndexFromUserFilteredChangesByKey(key);
      if (existingIdx !== null) {
        this.groupIndexMap.set(key, existingIdx);
        this.nextGroupIndex = Math.max(this.nextGroupIndex, existingIdx + 1);
        return existingIdx;
      }

      if (this.groupIndexMap.has(key)) {
        return this.groupIndexMap.get(key)!;
      }
    }

    const idx = this.nextGroupIndex++;
    if (key) {
      this.groupIndexMap.set(key, idx);
    }
    return idx;
  }

  private getGroupIndexFromUserFilteredChangesByKey(groupKey: string): number | null {
    const key = (groupKey || '').toString().trim().toLowerCase();
    if (!key) return null;
    const current = this._user.getFilteredChanges?.() ?? [];
    if (!Array.isArray(current) || current.length === 0) {
      return null;
    }
    const idx = current.findIndex((g: any) => this.normalizeGroupKey(g?.groupName) === key);
    return idx >= 0 ? idx : null;
  }

  private syncGroupIndexMapFromUserFilteredChanges(options?: { reset?: boolean }): void {
    if (options?.reset) {
      this.groupIndexMap.clear();
      this.nextGroupIndex = 0;
    }

    const current = this._user.getFilteredChanges?.() ?? [];
    if (!Array.isArray(current)) {
      return;
    }

    for (let i = 0; i < current.length; i++) {
      const name = (current[i]?.groupName ?? '').toString().trim();
      const key = this.normalizeGroupKey(name);
      if (!key) continue;
      this.groupIndexMap.set(key, i);
    }

    // Ensure newly-created indices don't overwrite existing loaded groups.
    this.nextGroupIndex = Math.max(this.nextGroupIndex, current.length);
  }

  private clearGroupChanges(groupName: string): void {
    const name = (groupName || '').toString().trim();
    if (!name) return;
    const key = this.normalizeGroupKey(name);
    const current = this._user.getFilteredChanges?.() ?? [];
    if (!Array.isArray(current) || !key) {
      return;
    }

    // Clear all entries that refer to this group (case-insensitive).
    for (let i = 0; i < current.length; i++) {
      const k = this.normalizeGroupKey(current[i]?.groupName);
      if (k && k === key) {
        this._user.removeFilteredChanges(i);
      }
    }
  }

  private removeSingleChangeLineFromGroup(groupName: string, summaryText: string): void {
    const name = (groupName || '').toString().trim();
    if (!name) return;
    this.removeSummaryLineFromUserFilteredChanges(name, summaryText);
  }

  private removeSummaryLineFromUserFilteredChanges(groupName: string, summaryText: string): void {
    const name = (groupName || '').toString().trim();
    const summary = (summaryText || '').toString().trim();
    if (!name || !summary) return;

    const key = this.normalizeGroupKey(name);
    const current = this._user.getFilteredChanges?.() ?? [];
    if (!key || !Array.isArray(current) || current.length === 0) {
      return;
    }

    const summaryKey = summary.toLowerCase();

    let idx = current.findIndex((g: any) => this.normalizeGroupKey(g?.groupName) === key);

    // Fallback: if group name doesn't resolve (edge-case timing), locate the summary line in any group.
    if (idx < 0) {
      idx = current.findIndex((g: any) => {
        const existing = Array.isArray(g?.changes) ? g.changes : [];
        return existing.some((c: any) => ((c?.summary ?? '').toString().trim().toLowerCase()) === summaryKey);
      });
    }

    if (idx < 0) {
      return;
    }

    const display = (current[idx]?.groupName ?? name).toString();
    const existing = Array.isArray(current[idx]?.changes) ? current[idx].changes : [];
    const nextChanges = existing.filter((c: any) => ((c?.summary ?? '').toString().trim().toLowerCase()) !== summaryKey);

    this._user.updateFilteredChanges(idx, display, nextChanges);
  }

  private removeGroupFromTree(groupName: string): void {
    const targetKey = this.normalizeGroupKey(groupName);
    const removeFrom = (nodes: any[] | null | undefined): any[] => {
      if (!Array.isArray(nodes)) return [];
      const out: any[] = [];
      for (const n of nodes) {
        if (this.normalizeGroupKey(n?.groupName) === targetKey) {
          continue;
        }
        if (Array.isArray(n?.members) && n.members.length) {
          n.members = removeFrom(n.members);
        }
        out.push(n);
      }
      return out;
    };

    if (Array.isArray(this.selectedTree)) {
      this.selectedTree = removeFrom(this.selectedTree);
    }
    if (Array.isArray(this.searchResults)) {
      this.searchResults = this.searchResults.filter((n: any) => this.normalizeGroupKey(n?.groupName) !== targetKey);
    }
  }

  private ensureGroupInTree(groupName: string): void {
    const name = (groupName || '').toString().trim();
    if (!name) return;
    const existing = this.findGroupInTree(Array.isArray(this.selectedTree) ? this.selectedTree : [], name);
    if (existing) return;

    const node = { groupName: name, members: [], asms: [] };
    if (!Array.isArray(this.selectedTree)) this.selectedTree = [];
    if (!Array.isArray(this.searchResults)) this.searchResults = [];
    this.selectedTree.unshift(node);
    this.searchResults.unshift(node);
    this.rebuildGroupSuggestionsIndex();
    this.rebuildObjectSuggestionsIndex();
  }

  private undoDeleteGroupChange(groupName: string): void {
    const name = (groupName || '').toString().trim();
    if (!name) return;
    // Restore visibility of the group in the tree from the baseline when available.
    this.restoreGroupFromBaseline(name);

    // Clear all staged changes for that group.
    this.clearGroupChanges(name);

    // Close editor if user was editing this group.
    if (this.selectedEditNode && this.normalizeGroupKey(this.selectedEditNode.groupName) === this.normalizeGroupKey(name)) {
      this.unselectEditNode();
    }
  }

  private undoCreateGroupChange(groupName: string): void {
    const name = (groupName || '').toString().trim();
    if (!name) return;
    const key = this.normalizeGroupKey(name);

    // If group didn't exist in the original device tree baseline, remove it.
    const existedInBaseline = !!(this.originalTree && this.findGroupInTree(this.originalTree, name));
    if (!existedInBaseline) {
      this.removeGroupFromTree(name);
    }

    // Clear staged changes and cached state.
    this.clearGroupChanges(name);
    this.pendingChangesByGroup.delete(key);
    this.editedNodesByGroup.delete(key);
    this.originalSnapshotsByGroup.delete(key);

    if (this.selectedEditNode && this.normalizeGroupKey(this.selectedEditNode.groupName) === key) {
      this.unselectEditNode();
    }
  }
  
  private processChangeRemoval(action: string, memberType: string, memberName: string) {
    if (!this.selectedEditNode) return;
    
    if (action === 'Add') {
      // Find and remove the added member from edit arrays
      const arrayToSearch = memberType === 'group' 
        ? this.selectedEditNode.memberGroups 
        : this.selectedEditNode.memberObjects;
      
      if (Array.isArray(arrayToSearch)) {
        const index = arrayToSearch.findIndex((m: any) => 
          (m.memberName || '').trim() === memberName.trim() &&
          (m.memberType || '').toLowerCase() === memberType
        );
        
        if (index !== -1) {
          const removedItem = arrayToSearch[index];
          this.clearIssue(removedItem);
          this.removedMembers.delete(removedItem);
          arrayToSearch.splice(index, 1);
          
          // Ensure there's still a trailing blank
          this.ensureOneTrailingBlank(arrayToSearch, memberType === 'group' ? 'group' : '');
          
          // Update diffs to reflect the removal
          this.updateEditDiffs();
        }
      }
    } else if (action === 'Remove') {
      // Find the member in removedMembers set and restore it
      const arrayToSearch = memberType === 'group' 
        ? this.selectedEditNode.memberGroups 
        : this.selectedEditNode.memberObjects;
      
      if (Array.isArray(arrayToSearch)) {
        const member = arrayToSearch.find((m: any) => 
          (m.memberName || '').trim() === memberName.trim() &&
          (m.memberType || '').toLowerCase() === memberType &&
          this.removedMembers.has(m)
        );
        
        if (member) {
          this.restoreMember(member);
        }
      }
    }
  }

  openForm(dataIn?: any) {
    // Logic to open a form or modal for editing/viewing details
    const dialogRef = this.dialog.open(AsaFormComponent, {
      width: '900px',
      maxWidth: 'calc(100vw - 10px)',
      maxHeight: 'calc(100vh - 10px)',
      panelClass: 'asa-form-dialog-panel',
      disableClose: true,
      autoFocus: false,
      data: dataIn
    });
  }

  toggleShowAllMembers(groupName: string) {
    if (this.showAllMembers.has(groupName)) {
      this.showAllMembers.delete(groupName);
    } else {
      this.showAllMembers.add(groupName);
    }
  }

  isShowingAllMembers(groupName: string): boolean {
    return this.showAllMembers.has(groupName);
  }

  getDisplayedMembers(obj: any): any[] {
    const members = obj.originalMembers || obj.members;
    if (!members || members.length === 0) return [];
    
    const term = (this.searchTerm || '').trim().toLowerCase();
    const isSearching = term.length >= 2;
    
    // Sort members by memberName (ascending, case-insensitive)
    const sortedMembers = [...members].sort((a: any, b: any) => {
      const nameA = (a.memberName || '').toLowerCase();
      const nameB = (b.memberName || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
    
    // If not searching, show all members
    if (!isSearching) {
      return sortedMembers;
    }
    
    // When searching: if showing all, return all members; otherwise return only matching members
    if (this.isShowingAllMembers(obj.groupName)) {
      return sortedMembers;
    }
    
    // Filter to only matching members
    return sortedMembers.filter((member: any) => 
      (member.memberName || '').toLowerCase().includes(term)
    );
  }

  hasMoreMembers(obj: any): boolean {
    const members = obj.originalMembers || obj.members;
    if (!members) return false;
    
    const term = (this.searchTerm || '').trim().toLowerCase();
    const isSearching = term.length >= 2;
    
    // Only show button when searching and there are filtered-out members
    if (!isSearching) return false;
    
    const matchingCount = members.filter((member: any) => 
      (member.memberName || '').toLowerCase().includes(term)
    ).length;
    
    return matchingCount < members.length;
  }

  getRemainingMembersCount(obj: any): number {
    const members = obj.originalMembers || obj.members;
    if (!members) return 0;
    
    const term = (this.searchTerm || '').trim().toLowerCase();
    const isSearching = term.length >= 2;
    
    if (!isSearching) return 0;
    
    const matchingCount = members.filter((member: any) => 
      (member.memberName || '').toLowerCase().includes(term)
    ).length;
    
    return members.length - matchingCount;
  }

  // Recursively searches the tree using targeted fields only
  filterTree(node: any, term: string, path: any[] = []): any | null {
    const lowerTerm = term.toLowerCase();
    // Only match on the tree node title (groupName)
    const title = (node.groupName || '').toLowerCase();
    const isMatch = title.includes(lowerTerm);

    let filteredmembers: any[] = [];
    if (node.members && node.members.length) {
      filteredmembers = node.members
        .map((child: any) => this.filterTree(child, term, [...path, node]))
        .filter((child: any) => child !== null);
    }

    if (isMatch || filteredmembers.length > 0) {
      path.forEach(n => this.expandedNodes.add(n.groupName));
      this.expandedNodes.add(node.groupName);
      return { ...node, members: filteredmembers };
    }
    return null;
  }

  // Highlight matched substrings for groupName only (tree node title)
  highlight(value: any): SafeHtml {
    if (value === null || value === undefined) return '' as any;
    const text = String(value);
    const term = (this.searchTerm || '').trim();
    if (term.length < 2) return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(text));
    const escapedText = this.escapeHtml(text);
    const escapedTerm = this.escapeRegex(term);
    const regex = new RegExp(escapedTerm, 'gi');
    const highlighted = escapedText.replace(regex, m => `<span class="search-highlight">${m}</span>`);
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  private escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  expandAllVisibleNodes(nodes: any[]) {
    for (const node of nodes) {
      this.expandedNodes.add(node.groupName);
      if (node.members && node.members.length) {
        this.expandAllVisibleNodes(node.members);
      }
    }
  }

  showAsms(asms: any) {
    this.selectedAsms = asms;
    this.selectedAsmsEntries = this.extractAsmsEntries(asms);
  }

  closeAsmsModal(): void {
    this.selectedAsms = null;
    this.selectedAsmsEntries = [];
  }

  trackByAsmsEntry = (index: number, entry: any): string => {
    const id = this.getAsmsId(entry);
    const group = (entry?.groupName ?? entry?.GroupName ?? '').toString();
    return `${id}::${group}::${index}`;
  };

  private extractAsmsEntries(source: any): any[] {
    if (!source) {
      return [];
    }

    // The View node typically looks like: { groupName, members, asms: [...] }
    const direct = (source as any).asms;
    if (Array.isArray(direct)) {
      return direct.filter(Boolean);
    }
    if (direct && typeof direct === 'object') {
      return [direct];
    }

    // Sometimes callers may pass the ASMS object/array directly.
    if (Array.isArray(source)) {
      return source.filter(Boolean);
    }
    if (typeof source === 'object') {
      const looksLikeAsms =
        (source as any).asmsID || (source as any).asmsId || (source as any).asmsNumber || (source as any).applicationName || (source as any).appName;
      return looksLikeAsms ? [source] : [];
    }

    return [];
  }

  getAsmsId(entry: any): string {
    const value = entry?.asmsID ?? entry?.asmsId ?? entry?.asmsNumber ?? entry?.asms_number ?? entry?.asmsIDNumber;
    const text = (value ?? '').toString().trim();
    return text || '—';
  }

  getAsmsAppName(entry: any): string {
    const value = entry?.applicationName ?? entry?.asmsAppName ?? entry?.appName ?? entry?.application_name;
    const text = (value ?? '').toString().trim();
    return text || '';
  }

  getAsmsDescription(entry: any): string | null {
    const value = entry?.appDescription ?? entry?.description ?? entry?.app_description;
    const text = (value ?? '').toString().trim();
    return text ? text : null;
  }

  getAsmsKeyValues(entry: any): Array<{ label: string; value: string }> {
    if (!entry || typeof entry !== 'object') {
      return [];
    }

    const pick = (label: string, ...keys: string[]): { label: string; value: string } | null => {
      for (const k of keys) {
        const v = (entry as any)[k];
        const text = (v ?? '').toString().trim();
        if (text) {
          return { label, value: text };
        }
      }
      return null;
    };

    const pairs: Array<{ label: string; value: string } | null> = [
      pick('Status', 'appStatus', 'status'),
      pick('Acronym', 'acronym'),
      pick('Ops Owner', 'OpsOwner', 'opsOwner'),
      pick('Business Owner', 'businessOwner', 'BusinessOwner'),
      pick('Innovation Owner', 'innovationOwner', 'InnovationOwner'),
      pick('Owning CIO', 'owningCIO', 'OwningCIO'),
      pick('Group', 'groupName', 'GroupName')
    ];

    return pairs.filter((p): p is { label: string; value: string } => !!p);
  }

  selectEditNode(node: any) {
    if (!this.requireChangeSelection('edit groups')) {
      return;
    }

    // Do not allow leaving the current edit session with unresolved blocking errors.
    // Exception: if the user is discarding a brand-new draft group, allow navigation (they will confirm).
    const isDraft = !!(this.selectedEditNode && (this.selectedEditNode as any).isRootDraft === true);
    if (this.selectedEditNode && !isDraft && this.hasBlockingEditErrors) {
      alert(`Resolve errors before switching groups:\n${this.getBlockingIssuesSummary() || 'Please fix the highlighted inputs.'}`);
      return;
    }

    // Ensure the group is expanded when entering edit mode so the editor is visible.
    try {
      const name = (node?.groupName ?? '').toString();
      if (name) {
        this.expandedNodes.add(name);
      }
    } catch {}

    // Guard against losing a Create New Group draft when switching groups
    if (this.selectedEditNode && (this.selectedEditNode as any).isRootDraft === true) {
      const proceed = window.confirm('You have an unsaved new group. Navigate away and lose this draft?');
      if (!proceed) {
        return;
      }
    }
    // 1) Sync current edits back to the node + tree before switching groups
    if (this.selectedEditNode && this.selectedEditNode.groupName) {
      const currentGroupName = this.selectedEditNode.groupName;
      const allEdits = [
        ...(this.selectedEditNode.memberGroups || []),
        ...(this.selectedEditNode.memberObjects || [])
      ].filter((m: any) => m && (m.memberName || '').trim() !== '');
      this.selectedEditNode.members = allEdits;

      const updateTree = (nodes: any[]) => {
        for (const n of nodes) {
          if (n.groupName === currentGroupName) {
            n.members = allEdits;
            return;
          }
          if (Array.isArray(n.members) && n.members.length) {
            updateTree(n.members);
          }
        }
      };
      if (Array.isArray(this.selectedTree)) updateTree(this.selectedTree);
      if (Array.isArray(this.searchResults)) updateTree(this.searchResults);
    }

    // 2) Determine group identity and mark active (case-insensitive)
    const groupName = node?.groupName || null;
    const groupKey = this.normalizeGroupKey(groupName);
    this.activeGroupKey = groupKey || null;

    // 3) Restore edited state if available; otherwise use provided node
    if (groupKey && this.editedNodesByGroup.has(groupKey)) {
      this.selectedEditNode = JSON.parse(JSON.stringify(this.editedNodesByGroup.get(groupKey)));
    } else {
      this.selectedEditNode = node;
    }
    this.selectedEditNode.memberType = 'group';

    // 4) Assign or retrieve group index for observable updates
    // IMPORTANT: resolve from UserService (source-of-truth) first to avoid races where
    // groupIndexMap isn't synced yet (e.g., remove-from-summary then navigate).
    if (groupKey) {
      const existingIdx = this.getGroupIndexFromUserFilteredChangesByKey(groupKey);
      if (existingIdx !== null) {
        this.selectedEditNodeIndex = existingIdx;
        this.groupIndexMap.set(groupKey, existingIdx);
        this.nextGroupIndex = Math.max(this.nextGroupIndex, existingIdx + 1);
      } else {
        if (!this.groupIndexMap.has(groupKey)) {
          this.groupIndexMap.set(groupKey, this.nextGroupIndex++);
        }
        this.selectedEditNodeIndex = this.groupIndexMap.get(groupKey)!;
      }
    } else {
      this.selectedEditNodeIndex = null;
    }

    // 5) Structure editing arrays; normalize memberType casing
    const hasStructured =
      Array.isArray((this.selectedEditNode as any).memberGroups) ||
      Array.isArray((this.selectedEditNode as any).memberObjects);

    if (!hasStructured) {
      const members = this.selectedEditNode?.members || [];
      members.forEach((m: any) => {
        if (m && m.memberType) m.memberType = m.memberType.toLowerCase();
      });
      (this.selectedEditNode as any).memberGroups = members.filter((m: any) => m.memberType === 'group');
      (this.selectedEditNode as any).memberObjects = members.filter((m: any) => m.memberType !== 'group');
    } else {
      const mg = (this.selectedEditNode as any).memberGroups || [];
      const mo = (this.selectedEditNode as any).memberObjects || [];
      mg.forEach((m: any) => { if (m && m.memberType) m.memberType = m.memberType.toLowerCase(); });
      mo.forEach((m: any) => { if (m && m.memberType) m.memberType = m.memberType.toLowerCase(); });
    }

    // 6) Baseline snapshot: per-group when available, else capture and store (BEFORE applying pending changes)
    // This ensures that CR's pending changes become the "current state" rather than the baseline
    if (groupKey && this.originalSnapshotsByGroup.has(groupKey)) {
      this.originalEditNodeSnapshot = JSON.parse(JSON.stringify(this.originalSnapshotsByGroup.get(groupKey)));
    } else {
      const snapshot = JSON.parse(JSON.stringify(this.selectedEditNode));
      this.originalEditNodeSnapshot = snapshot;
      if (groupKey) {
        this.originalSnapshotsByGroup.set(groupKey, snapshot);
      }
    }

    // 7) Reset removal state for a clean session
    this.removedMembers.clear();
    this.issuesByObject.clear();

    // 8) Align edit fields with any pending View Changes entries for this group
    if (groupName) {
      this.applyPendingChangesFromViewChanges(groupName);
    }

    // 9) Ensure one trailing blank input in both lists
    if (Array.isArray((this.selectedEditNode as any).memberGroups)) {
      this.dedupeMemberList((this.selectedEditNode as any).memberGroups, 'group');
      this.ensureOneTrailingBlank((this.selectedEditNode as any).memberGroups, 'group');
    }
    if (Array.isArray((this.selectedEditNode as any).memberObjects)) {
      this.dedupeMemberList((this.selectedEditNode as any).memberObjects, '');
      this.ensureOneTrailingBlank((this.selectedEditNode as any).memberObjects, '');
    }

    // 10) Recompute summaries and aggregated changes
    // Recompute and publish diffs via observable; View Changes derives from filteredChanges$
    this.recomputeInlineSummary();
  }

  getInlineDraft(node: any): { value: string; type: string } {
    let draft = this.inlineDrafts.get(node);
    if (!draft) {
      draft = { value: '', type: '' };
      this.inlineDrafts.set(node, draft);
    }
    return draft;
  }

  addInlineChild(node: any) {
    const draft = this.getInlineDraft(node);
    const value = (draft.value || '').trim();
    if (!value) {
      return;
    }

    if (!Array.isArray(node.members)) {
      node.members = [];
    }

    const type = draft.type || 'group';
    const parentName = node.groupName || node.value || '';

    const newNode = {
      groupName: type === 'group' ? value : undefined,
      value,
      type,
      parent: parentName,
      members: type === 'group' ? [] : undefined
    } as any;

    node.members.push(newNode);
    this.newlyAddedNodes.add(newNode);

    draft.value = '';
    draft.type = '';

    this.recomputeInlineSummary();
  }

  removeInlineNode(target: any, parent?: any) {
    const container = parent ? parent.members : this.selectedTree;
    if (!Array.isArray(container)) return;
    const index = container.indexOf(target);
    if (index > -1) {
      container.splice(index, 1);
      this.inlineDrafts.delete(target);
      this.recomputeInlineSummary();
    }
  }

  openInventoryDialog() {
    if (this.selectedEditNode && (this.selectedEditNode as any).isRootDraft === true) {
      const proceed = window.confirm('You have an unsaved new group. Navigate away and lose this draft?');
      if (!proceed) {
        return;
      }
    }
    this.inventoryDialogOpen = true;
  }

  closeInventoryDialog() {
    this.inventoryDialogOpen = false;
  }

  async setGoldSource() {
    if (this.isSettingGoldSource) {
      return;
    }

    const serial = (this.selectedInventory?.serialNumber ?? '').toString().trim();
    const hostname = (this.selectedInventory?.hostname ?? '').toString().trim();
    if (!serial) {
      window.alert('Unable to set Gold Source: selected device serial number is missing.');
      return;
    }

    if (this.selectedInventory?.masterConfig === 1) {
      return;
    }

    const confirmed = window.confirm(
      `Set${hostname ? ' ' + hostname : ''} as Gold Source? This will replace the current Gold Source device.`
    );
    if (!confirmed) {
      return;
    }

    this.isSettingGoldSource = true;
    try {
      await firstValueFrom(this._db.setGoldSource(serial));

      // Update local state so badges + sorting reflect immediately.
      if (Array.isArray(this.allInventory)) {
        for (const item of this.allInventory) {
          if (!item) continue;
          item.masterConfig = (item.serialNumber === serial) ? 1 : 0;
        }
      }
      if (this.selectedInventory) {
        this.selectedInventory.masterConfig = 1;
      }
    } catch (e) {
      console.error('Unable to set Gold Source', e);
      const status = (e as any)?.status;
      if (status === 404) {
        window.alert('Unable to set Gold Source: this environment does not support that API yet (404). The backend needs a PUT endpoint for setting Gold Source.');
      } else {
        window.alert('Unable to set Gold Source. Please try again, or contact support if the problem persists.');
      }
    } finally {
      this.isSettingGoldSource = false;
    }
  }

  /**
   * Ensure groups from loaded changes exist in the tree structure.
   * If a group doesn't exist, create it at the top of the tree.
   */
  private ensureGroupsExistInTree(changes: Array<{ groupName: string; changes: Array<{ summary: string; meta?: string }> }>) {
    if (!Array.isArray(this.selectedTree)) {
      this.selectedTree = [];
    }
    if (!Array.isArray(this.searchResults)) {
      this.searchResults = [];
    }

    for (const changeGroup of changes) {
      const groupName = changeGroup.groupName?.trim();
      if (!groupName) continue; // Skip empty group names

      // Skip empty groups entirely; they should not affect the tree.
      if (!Array.isArray(changeGroup.changes) || changeGroup.changes.length === 0) {
        continue;
      }

      const groupKey = this.normalizeGroupKey(groupName);

      // Check if group already exists in the tree
      let groupNode = this.findGroupInTree(this.selectedTree, groupName);
      
      // Only materialize a missing group into the left tree if there is a meaningful
      // "positive" change that benefits from showing it (e.g., Create/Add/Rename).
      // If the only change is deleting/removing, we should NOT resurrect a phantom group.
      const shouldMaterializeMissingGroup = (changeGroup.changes || []).some((c: any) => {
        const s = (c?.summary || '').toString().trim().toLowerCase();
        return s.startsWith('create group:') || s.startsWith('add ')
          || s.startsWith('rename subgroup:');
      });

      if (!groupNode && shouldMaterializeMissingGroup) {
        // Create new group at the top of the tree
        groupNode = {
          groupName: groupName,
          members: [],
          asms: []
        };
        
        // Add to top of both selectedTree and searchResults
        this.selectedTree.unshift(groupNode);
        this.searchResults.unshift(groupNode);
      }

      // IMPORTANT: do not mutate baseline tree members using CR pending changes.
      // Pending changes should be applied only in edit mode via applyPendingChangesFromViewChanges().
      
      // Set up group index mapping for this group
      if (groupKey && !this.groupIndexMap.has(groupKey)) {
        this.groupIndexMap.set(groupKey, this.nextGroupIndex++);
      }
    }
    
    // Update group name suggestions list
    this.rebuildGroupSuggestionsIndex();
    this.rebuildObjectSuggestionsIndex();
  }

  /**
   * Recursively search for a group by name in the tree structure
   */
  private findGroupInTree(nodes: any[], groupName: string): any {
    if (!Array.isArray(nodes)) return null;

    const targetKey = this.normalizeGroupKey(groupName);
    
    for (const node of nodes) {
      if (this.normalizeGroupKey(node.groupName) === targetKey) {
        return node;
      }
      
      // Search recursively in members if they exist
      if (Array.isArray(node.members) && node.members.length > 0) {
        const found = this.findGroupInTree(node.members, groupName);
        if (found) return found;
      }
    }
    
    return null;
  }

  // Recursively sort tree nodes by groupName (case-insensitive)
  private sortTree(nodes: any[]): any[] {
    if (!Array.isArray(nodes)) return [];
    const sorted = [...nodes].sort((a, b) => {
      const ag = (a?.groupName || '').toLowerCase();
      const bg = (b?.groupName || '').toLowerCase();
      if (ag < bg) return -1;
      if (ag > bg) return 1;
      return 0;
    });
    return sorted.map(n => {
      if (n.members && Array.isArray(n.members) && n.members.length) {
        return { ...n, members: this.sortTree(n.members) };
      }
      return n;
    });
  }

  private recomputeInlineSummary() {
    // Do not publish draft changes here; only publish upon explicit Create.
    if (!this.selectedTree || !Array.isArray(this.selectedTree)) {
      return;
    }

    const isRootDraft = !!(this.selectedEditNode && (this.selectedEditNode as any).isRootDraft === true);
    if (!isRootDraft) {
      // Normal edits are handled by updateEditDiffs(); nothing to do here.
      return;
    }

    const toCli = (item: any, parentName?: string): any | null => {
      if (!item) return null;
      const value = item.groupName || item.value || '';
      const type = item.type || (item.members && item.members.length ? 'group' : 'object');
      const parent = parentName || item.parent || '';
      const members = Array.isArray(item.members)
        ? item.members.map((child: any) => toCli(child, value)).filter(Boolean)
        : [];
      return { value, type, parent, members };
    };

    // Only consider nodes explicitly added in this session as changes
    const diffItems: any[] = [];

    const walkNew = (nodes: any[], parentName?: string) => {
      for (const node of nodes) {
        if (this.newlyAddedNodes.has(node)) {
          const converted = toCli(node, parentName);
          if (converted) {
            diffItems.push(converted);
          }
        }
        if (Array.isArray(node.members) && node.members.length) {
          const name = node.groupName || node.value || '';
          walkNew(node.members, name);
        }
      }
    };

    walkNew(this.selectedTree);

    // Draft flow: compute summaries if needed for preview, but do not publish yet.
  }

  /**
   * Save the current tree edits for the selected group directly into
   * the active Change Request (identified by crNumber in route params).
   */
  saveTreeEditsToChange() {
    // Prevent multiple simultaneous saves
    if (this.isSavingChanges) {
      return;
    }

    // Reset visual save indicator for this attempt.
    this.treeEditsSaved = false;

    if (!this.requireChangeSelection('save tree edits')) {
      return;
    }

    if (this.hasBlockingEditErrors) {
      alert(`Fix errors before saving:\n${this.getBlockingIssuesSummary() || 'Please fix the highlighted inputs.'}`);
      return;
    }

    this.isSavingChanges = true;
    this.newCrNumberError = '';

    // Snapshot the current filtered changes in the shape expected by both View and Change Control.
    // Use the already-cleaned view of changes to avoid persisting empty placeholder groups.
    const hostname = (this.selectedInventory?.hostname ?? '').toString().trim();
    const serialNumber = (this.selectedInventory?.serialNumber ?? '').toString().trim();
    const cliConfigToSave: Array<{ groupName: string; changes: Array<{ summary: string; meta?: string }>; hostname?: string; serialNumber?: string }> =
      Array.isArray(this.allFilteredChanges)
        ? this.allFilteredChanges.map(g => ({
            groupName: (g?.groupName ?? '').toString(),
            changes: Array.isArray(g?.changes) ? g.changes.map((c: any) => ({ summary: c.summary, meta: c.meta })) : [],
            hostname: hostname || undefined,
            serialNumber: serialNumber || undefined
          }))
        : [];

     if (this.showNewCrInput && this.newCrNumber.trim()) {
      if (this.isCrNumberDuplicate) {
        this.newCrNumberError = 'CR already exists';
        this.isSavingChanges = false;
        return;
      }

      const gmid = this.getCurrentUserGmid();
      if (!gmid) {
        this.newCrNumberError = 'User information is missing. Please sign in again or reload the page.';
        this.isSavingChanges = false;
        return;
      }

      // Create new change with this cliConfig
      let newChange = {
        crNumber: this.newCrNumber.trim(),
        cliConfig: cliConfigToSave,
        gmid,
        submissionStatus: 'Active',
        itemDescription: ''
      };
      this._change.createNewChange(newChange).subscribe({
        next: async (res: any) => {
          const createdCr = (res?.crNumber ?? newChange.crNumber ?? '').toString().trim();
          this.showNewCrInput = false;
          this.newCrNumber = '';

          // Reload from backend so cliConfig shape matches what onChangeSelected/loadFilteredChanges expects.
          try {
            await this.refreshActiveChanges();
          } catch {}

          if (createdCr) {
            this.currentCrNumber = createdCr;
            this.onChangeSelected(createdCr, { suppressConfirm: true });
          }
          this.treeEditsSaved = true;
          this.isSavingChanges = false;
        },
        error: (err: any) => {
          console.error('Failed to create new change with tree edits:', err);
          if (err?.message === 'DUPLICATE_CR_NUMBER') {
            this.newCrNumberError = 'CR already exists';
          } else if (err?.message === 'CR_NUMBER_REQUIRED') {
            this.newCrNumberError = 'New Change ID is required';
          } else {
            this.newCrNumberError = 'Failed to create new change';
          }
          this.isSavingChanges = false;
        }
      });
      return;
    } else if (this.showNewCrInput && !this.newCrNumber.trim()) {
      console.error('No CR number provided for new change creation');
      this.newCrNumberError = 'New Change ID is required';
      this.isSavingChanges = false;
      return;
    } else if (!this.currentCrNumber) {
      console.error('No CR number available in route for change update');
      this.isSavingChanges = false;
      return;
    } else {
      // update the existing change with crNumber of selectedCrNumber. Get change from this.activeChanges
      let change = this.activeChanges.find(c => c.crNumber === this.currentCrNumber);
      if (!change) {
        console.error('Could not find change with CR number:', this.currentCrNumber);
        this.isSavingChanges = false;
        return;
      }
      change.cliConfig = cliConfigToSave;
      this._change.editChange(change).subscribe({
        next: (res: any) => {
          this.treeEditsSaved = true;
          this.isSavingChanges = false;
        },
        error: (err: any) => {
          console.error('Failed to save tree edits to change:', err);
          this.isSavingChanges = false;
        }
      });
    }


    // this.currentCrNumber = 'CR-test-9008';

    // if (!this.currentCrNumber) {
    //   console.error('No CR number available in route for change update');
    //   return;
    // }

    // // this._change.getChangeByCrNumber(this.currentCrNumber).subscribe({
    // this._change.getAllChanges().subscribe({
    //   next: (changes: any) => {

    //     // get change by crnumber from this list
    //     const change = changes.find((c: any) => c.crNumber === this.currentCrNumber);
    //     if (!change) {
    //       console.error('Change not found for CR', this.currentCrNumber);
    //       return;
    //     }

    //     change.cliConfig = JSON.stringify(this._user.getFilteredChanges());

    //     this._change.editChange(change).subscribe({
    //       next: (res: any) => {
    //       },
    //       error: (err: any) => {
    //         console.error('Failed to save tree edits to change:', err);
    //       }
    //     });
    //   },
    //   error: (err: any) => {
    //     console.error('Error loading change by CR number:', err);
    //   }
    // });
  }
  
  // Collect all unique group names from tree for suggestions
  private extractAllGroupNames(nodes: any[]): string[] {
    const names = new Set<string>();
    const walk = (arr: any[]) => {
      if (!Array.isArray(arr)) return;
      for (const n of arr) {
        const name = (n?.groupName || '').toString().trim();
        if (name) names.add(name);
        // Some tree shapes use `children`; others nest group nodes under `members`.
        if (Array.isArray(n?.children) && n.children.length) {
          walk(n.children);
        }
        if (Array.isArray(n?.members) && n.members.length) {
          const looksLikeGroupNodes = n.members.some((m: any) => m && typeof m === 'object' && 'groupName' in m);
          if (looksLikeGroupNodes) {
            walk(n.members);
          }
        }
      }
    };
    walk(nodes);
    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  private rebuildGroupSuggestionsIndex(): void {
    const src = Array.isArray(this.selectedTree) ? this.selectedTree : [];
    this.allGroupNames = this.extractAllGroupNames(src);
    this.allGroupNamesLower = this.allGroupNames.map((name) => name.toLowerCase());
    this.allGroupNamesSet = new Set(this.allGroupNamesLower);
    this.groupSuggestionCache.clear();
  }

  private rebuildObjectSuggestionsIndex(): void {
    const src = Array.isArray(this.originalTree)
      ? this.originalTree
      : (Array.isArray(this.selectedTree) ? this.selectedTree : []);
    this.allObjectValues = this.extractAllObjectValues(src);
    this.allObjectValuesLower = this.allObjectValues.map((value) => value.toLowerCase());
    this.objectSuggestionCache.clear();
  }

  private extractAllObjectValues(nodes: any[]): string[] {
    const values = new Set<string>();

    const walk = (arr: any[]) => {
      if (!Array.isArray(arr)) return;
      for (const n of arr) {
        const members = Array.isArray(n?.members) ? n.members : [];
        for (const m of members) {
          if (!m) continue;

          const memberName = (m?.memberName ?? m?.value ?? '').toString().trim();
          if (!memberName) continue;

          const rawType = (m?.memberType ?? m?.type ?? '').toString().trim().toLowerCase();
          if (rawType === 'group') continue;

          const detectedType = rawType && rawType !== 'unknown'
            ? rawType
            : (this.matchInputType(memberName) || 'unknown');
          if (detectedType === 'unknown' || detectedType === 'group') continue;

          values.add(memberName);
        }

        if (Array.isArray(n?.children) && n.children.length) {
          walk(n.children);
        }
        if (Array.isArray(n?.members) && n.members.length) {
          const looksLikeGroupNodes = n.members.some((m: any) => m && typeof m === 'object' && 'groupName' in m);
          if (looksLikeGroupNodes) {
            walk(n.members);
          }
        }
      }
    };

    walk(nodes);
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }
  
  // Create a brand new root group and enter edit mode using existing flow
  createNewRootGroup() {
    if (!this.requireChangeSelection('create a new group')) {
      return;
    }
    const draftRoot: any = {
      groupName: '',
      memberType: 'group',
      isRootDraft: true,
      members: [],
      memberGroups: [],
      memberObjects: []
    };

    // Enter edit mode on the draft root using the same editor
    this.selectedEditNode = draftRoot;
    
    // Assign a new index for this draft group
    const tempGroupName = 'new-root-' + this.nextGroupIndex;
    this.groupIndexMap.set(tempGroupName, this.nextGroupIndex);
    this.selectedEditNodeIndex = this.nextGroupIndex++;
    
    this.originalEditNodeSnapshot = JSON.parse(JSON.stringify(draftRoot));

    // Ensure one trailing blank input for both groups and objects
    this.ensureOneTrailingBlank(this.selectedEditNode.memberGroups, 'group');
    this.ensureOneTrailingBlank(this.selectedEditNode.memberObjects, '');
    this.updateEditDiffs();
  }

  // Confirm creation of the draft root group: add to top of tree and close draft
  confirmCreateNewRootGroup() {
    if (!this.selectedEditNode || this.selectedEditNode.isRootDraft !== true) return;
    const name = (this.selectedEditNode.groupName || '').toString().trim();
    if (!name) return;

    // Disallow duplicates (case-insensitive) to avoid confusing duplicates in the tree and right panel.
    const existing = this.findGroupInTree(Array.isArray(this.selectedTree) ? this.selectedTree : [], name);
    if (existing) {
      alert(`Group "${name}" already exists. Please choose a unique group name.`);
      return;
    }
    // Build members array from structured inputs
    const toMember = (item: any) => ({
      memberName: (item.memberName || item.value || '').toString(),
      memberType: (item.memberType || item.type || '').toString().toLowerCase()
    });
    const members: any[] = [];

    // Validate inputs (duplicates + unknown groups) before creation.
    this.validateMemberList(this.selectedEditNode.memberGroups, 'group');
    this.validateMemberList(this.selectedEditNode.memberObjects, '');
    if (this.hasBlockingEditErrors) {
      alert(`Fix errors before creating the group:\n${this.getBlockingIssuesSummary() || 'Please fix the highlighted inputs.'}`);
      return;
    }
    if (Array.isArray(this.selectedEditNode.memberGroups)) {
      const invalidGroupMembers = (this.selectedEditNode.memberGroups || [])
        .map((g: any) => (g?.memberName ?? '').toString().trim())
        .filter((v: string) => v && !this.isExistingGroupName(v));
      if (invalidGroupMembers.length > 0) {
        alert(`Only existing groups can be added as group members. Unknown group(s): ${invalidGroupMembers.join(', ')}`);
        return;
      }
      for (const g of this.selectedEditNode.memberGroups) {
        const v = (g?.memberName || '').toString().trim();
        if (v && this.isExistingGroupName(v)) members.push(toMember(g));
      }
    }
    if (Array.isArray(this.selectedEditNode.memberObjects)) {
      for (const o of this.selectedEditNode.memberObjects) {
        const v = (o?.memberName || '').toString().trim();
        if (v) members.push(toMember(o));
      }
    }
    const newNode: any = {
      groupName: name,
      members,
      asms: []
    };
    // Insert at top of selectedTree and searchResults
    if (!Array.isArray(this.selectedTree)) this.selectedTree = [];
    this.selectedTree.unshift(newNode);
    if (!Array.isArray(this.searchResults)) this.searchResults = [];
    this.searchResults.unshift(newNode);
    
    // Publish the creation to the observable change list now that user confirmed
    const summaries: Array<{ summary: string; meta?: string }> = [];
    summaries.push({ summary: `Create group: ${name}`, meta: `Group • ${name}` });
    for (const m of members) {
      const v = (m.memberName || '').toString().trim();
      const t = (m.memberType || 'unknown').toString().toLowerCase();
      if (v) {
        const normalized = this.normalizeValueForChangeSummary(t, v);
        summaries.push({ summary: `Add ${t}: ${normalized}`, meta: `Under ${name}` });
      }
    }
    const nameKey = this.normalizeGroupKey(name);
    const idx = this.groupIndexMap.get(nameKey) ?? this.nextGroupIndex++;
    this.groupIndexMap.set(nameKey, idx);
    this.selectedEditNodeIndex = idx;
    this._user.updateFilteredChanges(idx, name, summaries);
    // Persist edited state and baseline for future edits
    this.editedNodesByGroup.set(nameKey, {
      groupName: name,
      memberType: 'group',
      memberGroups: [...(this.selectedEditNode.memberGroups || [])],
      memberObjects: [...(this.selectedEditNode.memberObjects || [])]
    });
    if (!this.originalSnapshotsByGroup.has(nameKey)) {
      this.originalSnapshotsByGroup.set(nameKey, JSON.parse(JSON.stringify(newNode)));
    }
    // Clear draft and show create button again
    this.selectedEditNode = null;
    this.updateEditDiffs();
  }
  
  unselectEditNode() {
    const isDraft = !!(this.selectedEditNode && (this.selectedEditNode as any).isRootDraft === true);
    if (!isDraft) {
      this.revalidateSelectedEditNode();
    }
    if (!isDraft && this.hasBlockingEditErrors) {
      alert(`Resolve errors before closing edit:\n${this.getBlockingIssuesSummary() || 'Please fix the highlighted inputs.'}`);
      return;
    }

    // Prevent closing the Create New Group draft without explicit action
    if (this.selectedEditNode && (this.selectedEditNode as any).isRootDraft === true) {
      const proceed = window.confirm('Are you sure you want to navigate away? You will lose the data in the new group you have created.');
      if (!proceed) {
        return;
      }
    }
    this.selectedEditNode = null;
    this.selectedEditNodeIndex = null;
    this.removedMembers.clear();
    this.issuesByObject.clear();
  }
  
  matchInputType(input: string): string | null {
    const ip = '(?:25[0-5]|2[0-4]\\d|1?\\d{1,2})';
    const ipV4 = `(?:${ip}\\.){3}${ip}`;
    const regexPatterns: { [key: string]: RegExp } = {
      fqdn: /^(?:[a-zA-Z0-9_-]+\.)+[a-zA-Z][a-zA-Z0-9_-]*$/,
      host: new RegExp(`^${ipV4}$`),
      subnet: /^\s*(\d{1,3}\.){3}\d{1,3}(\s+(255|254|252|248|240|224|192|128|0)\.(255|254|252|248|240|224|192|128|0)\.(255|254|252|248|240|224|192|128|0)\.(0|128|192|224|240|248|252|254|255)|\/([8-9]|[1-2][0-9]|3[0-2]))\s*$/,
      // Accept: "10.0.0.1 10.0.0.2", "10.0.0.1-10.0.0.2", "r-10.0.0.1-10.0.0.2", optional "Range:" prefix
      range: new RegExp(`^\\s*(?:range\\s*:\\s*)?(?:r\\s*-\\s*)?(${ipV4})\\s*(?:-|\\s+)\\s*(${ipV4})\\s*$`, 'i')
    };

    // Check each pattern
    for (const type in regexPatterns) {
      if (regexPatterns[type].test(input)) {
        return type;
      }
    }

    return 'unknown';
  }

  onInputChangeDebounced(obj: any, list?: any[], typeHint?: string) {
    if (!obj) return;
    const isGroup = obj.memberType === 'group' || typeHint === 'group';
    if (isGroup) {
      obj.memberType = 'group';
    }
    this.scheduleInputChange(obj, list, isGroup ? 'group' : (typeHint ?? obj.memberType ?? ''));
  }

  private scheduleInputChange(obj: any, list?: any[], typeHint?: string) {
    const existing = this.inputDebounceTimers.get(obj);
    if (existing) {
      clearTimeout(existing);
    }
    const timer = setTimeout(() => {
      this.inputDebounceTimers.delete(obj);
      this.onInputChange(obj, list, typeHint);
    }, this.inputDebounceMs);
    this.inputDebounceTimers.set(obj, timer);
  }

  private onInputChange(obj: any, list?: any[], typeHint?: string) {
    if (!obj) return;
    const isGroup = obj.memberType === 'group' || typeHint === 'group';
    if (isGroup) {
      obj.memberType = 'group';
      this.updateChildrenParentName(obj);
    }

    this.ensureOneTrailingBlank(list, isGroup ? 'group' : (typeHint ?? obj.memberType ?? ''));

    // Validate inputs + duplicates without deleting what the user typed.
    this.validateMemberList(list, isGroup ? 'group' : '');
    this.ensureOneTrailingBlank(list, isGroup ? 'group' : (typeHint ?? obj.memberType ?? ''));

    // After input changes, recompute diffs against original snapshot
    this.updateEditDiffs(true);

    // If the user cleared the field, shift focus to the trailing blank input
    const trimmed = (obj.memberName ?? '').toString().trim();
    if (Array.isArray(list) && trimmed === '') {
      // Clear any previous requestFocus flags
      list.forEach(item => { if (item) item.requestFocus = false; });
      const last = list[list.length - 1];
      if (last) {
        // Ensure last has a stable uid for DOM lookup
        if (!last.uid) {
          last.uid = `input-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        last.requestFocus = true;
        // Update focus tracking to the new last blank
        this.setInputFocus(last, true);
        this.setInputFocus(obj, false);
        // Focus the actual input element corresponding to the last blank
        this.focusItemInput(last);
      }
    }
  }

  /**
   * Determine if a member is being added or removed
   * Returns 'add', 'remove', or null
   */
  getMemberAction(member: any): 'add' | 'remove' | null {
    if (!member) {
      return null;
    }

    const memberName = (member.memberName || '').trim();
    if (!memberName) {
      return null; // Empty/blank members don't get a label
    }
    
    // Check if marked for removal
    if (this.removedMembers.has(member)) {
      return 'remove';
    }
    
    // Check if it's a new addition
    if (!this.originalEditNodeSnapshot) {
      return null;
    }
    
    const memberType = (member.memberType || '').toLowerCase();
    const originalMembers = this.originalEditNodeSnapshot.members || [];
    
    const existedBefore = originalMembers.some((m: any) => 
      (m.memberName || '').trim() === memberName &&
      (m.memberType || '').toLowerCase() === memberType
    );

    if (!existedBefore) {
      return 'add';
    }
    
    return null;
  }

  isMemberEditDisabled(member: any): boolean {
    if (this.isEditFlowBlocked) {
      return true;
    }
    const memberName = (member?.memberName || '').toString().trim();
    if (!memberName) {
      return false; // blank rows are editable
    }
    // Rows created as new blank inputs by the user are always editable, even if
    // the value they typed happens to match an existing member.
    if (member?._isNewRow === true) {
      return false;
    }
    if (!this.originalEditNodeSnapshot) {
      return false;
    }
    // Only brand-new values can be edited; existing values can only be removed/restored.
    return this.getMemberAction(member) !== 'add';
  }
  
  /**
   * Mark a member for removal (shows Remove tag but keeps it visible)
   */
  removeMember(member: any) {
    if (!this.requireChangeSelection('edit group members')) {
      return;
    }
    const memberName = (member?.memberName || '').toString().trim();
    if (!memberName || !this.selectedEditNode || !this.originalEditNodeSnapshot) {
      return;
    }

    const memberType = (member?.memberType || '').toString().toLowerCase();
    const originalMembers = this.originalEditNodeSnapshot.members || [];
    const isGroup = memberType === 'group';
    const typeHint = isGroup ? 'group' : '';

    const tryRemoveFrom = (arr: any[] | undefined, hint: string) => {
      if (!Array.isArray(arr)) return false;
      let idx = arr.indexOf(member);
      if (idx === -1) {
        idx = arr.findIndex((m: any) =>
          (m?.memberName || '').toString().trim() === memberName &&
          (m?.memberType || '').toString().toLowerCase() === memberType
        );
      }
      if (idx === -1) return false;
      const removedItem = arr[idx];
      this.clearIssue(removedItem);
      arr.splice(idx, 1);
      this.removedMembers.delete(member);
      this.ensureOneTrailingBlank(arr, hint);
      return true;
    };

    const list = isGroup ? this.selectedEditNode.memberGroups : this.selectedEditNode.memberObjects;
    const targetKey = this.getMemberDedupeKey(member, typeHint);
    if (targetKey && Array.isArray(list)) {
      const matches = list.filter((m: any) =>
        m && !this.removedMembers.has(m) && this.getMemberDedupeKey(m, typeHint) === targetKey
      ).length;
      if (matches > 1) {
        if (tryRemoveFrom(list, typeHint)) {
          this.updateEditDiffs();
        }
        return;
      }
    }

    const existedBeforeExact = originalMembers.some((m: any) =>
      (m?.memberName || '').toString().trim() === memberName &&
      (m?.memberType || '').toString().toLowerCase() === memberType
    );
    const existedBeforeByNameOnly = !memberType && originalMembers.some((m: any) =>
      (m?.memberName || '').toString().trim() === memberName
    );
    const existedBefore = existedBeforeExact || existedBeforeByNameOnly;

    // If this was just added in this edit session (not in the baseline snapshot),
    // remove it entirely instead of creating a new "Remove" change.
    if (!existedBefore) {
      const removed =
        tryRemoveFrom(this.selectedEditNode.memberGroups, 'group') ||
        tryRemoveFrom(this.selectedEditNode.memberObjects, '');

      if (removed) {
        this.updateEditDiffs();
      }
      return;
    }

    this.removedMembers.add(member);
    this.updateEditDiffs();
  }
  
  /**
   * Restore a member that was marked for removal
   */
  restoreMember(member: any) {
    if (!this.requireChangeSelection('edit group members')) {
      return;
    }
    this.removedMembers.delete(member);
    this.updateEditDiffs();
  }

  /**
    * Delete an entire group
   */
  deleteGroup(group: any) {
    if (!this.requireChangeSelection('delete groups')) {
      return;
    }
    if (!group || !group.groupName) {
      console.error('Invalid group for deletion');
      return;
    }

    const groupName = group.groupName.trim();
    const groupKey = this.normalizeGroupKey(groupName);

    const existedInBaseline = !!(this.originalTree && this.findGroupInTree(this.originalTree, groupName));
    const confirmMessage = existedInBaseline
      ? `Are you sure you want to delete the group "${groupName}"? This action will create a delete change entry.`
      : `The group "${groupName}" is not in Gold Source. Deleting it will discard this staged group and its staged changes. Continue?`;
    const confirmDelete = confirm(confirmMessage);
    
    if (!confirmDelete) {
      return;
    }

    // If group doesn't exist in the baseline tree, treat delete as "discard staged group".
    // This avoids recording "Delete group" for something that never existed in Gold Source.
    if (!existedInBaseline) {
      this.removeGroupFromTree(groupName);
      this.clearGroupChanges(groupName);
      this.pendingChangesByGroup.delete(groupKey);
      this.editedNodesByGroup.delete(groupKey);
      this.originalSnapshotsByGroup.delete(groupKey);
      this.groupIndexMap.delete(groupKey);

      if (this.selectedEditNode && this.normalizeGroupKey(this.selectedEditNode.groupName) === groupKey) {
        this.unselectEditNode();
      }
      return;
    }

    // Get or create group index for this group
    let groupIndex: number;
    if (!this.groupIndexMap.has(groupKey)) {
      groupIndex = this.nextGroupIndex++;
      this.groupIndexMap.set(groupKey, groupIndex);
    } else {
      groupIndex = this.groupIndexMap.get(groupKey)!;
    }

    // Keep behavior minimal: deleting a group only stages the group deletion itself.
    const changes: Array<{ summary: string; meta?: string }> = this.buildRecursiveDeleteGroupChanges(groupName);

    // Publish changes to the UserService with group name and index
    this._user.updateFilteredChanges(groupIndex, groupName, changes);

    // Remove the group from the visible tree so it doesn't remain on the left.
    this.removeGroupFromTree(groupName);

    // Close the edit panel if it was open
    this.unselectEditNode();
  }

  /**
   * Sort changes by type and action (add/remove)
   * Order: add group, remove group, add fqdn, remove fqdn, add host, remove host, add unknown, remove unknown
   */
  private sortChanges(changes: Array<{ summary: string; meta?: string }>): Array<{ summary: string; meta?: string }> {
    const getTypeOrder = (summary: string): number => {
      const lower = summary.toLowerCase();
      
      // Determine if it's add or remove (0 = add, 1 = remove)
      const isRemove = lower.startsWith('remove') || lower.startsWith('delete') ? 1 : 0;
      
      // Determine type (group, fqdn, host/ip, unknown)
      let typeBase = 7; // default unknown
      if (lower.includes('group')) {
        typeBase = 0;
      } else if (lower.includes('fqdn')) {
        typeBase = 2;
      } else if (lower.includes('host') || lower.includes('ip')) {
        typeBase = 4;
      } else if (lower.includes('object') || lower.includes('unknown')) {
        typeBase = 6;
      }
      
      // Combine type and action: add group=0, remove group=1, add fqdn=2, remove fqdn=3, etc.
      return typeBase + isRemove;
    };
    
    return [...changes].sort((a, b) => {
      const orderA = getTypeOrder(a.summary);
      const orderB = getTypeOrder(b.summary);
      return orderA - orderB;
    });
  }

  // Return top 5 closest group name matches to the current input
  getGroupSuggestions(query: string | undefined): string[] {
    const q = this.normalizeSuggestionQuery(query);
    if (!q) return [];
    const cached = this.groupSuggestionCache.get(q);
    if (cached) return cached;
    const starts: string[] = [];
    const contains: string[] = [];
    for (let i = 0; i < this.allGroupNames.length; i++) {
      const name = this.allGroupNames[i];
      const n = this.allGroupNamesLower[i];
      if (n.startsWith(q)) {
        starts.push(name);
      } else if (n.includes(q)) {
        contains.push(name);
      }
      if (starts.length >= 5) break;
    }
    const combined = starts.concat(contains);
    // Deduplicate and cap to 5
    const seen = new Set<string>();
    const result: string[] = [];
    for (const s of combined) {
      if (!seen.has(s)) {
        seen.add(s);
        result.push(s);
        if (result.length >= 5) break;
      }
    }
    // Hide suggestions when all suggestions are exact matches to the query
    const hasNonExact = result.some(s => s.toLowerCase() !== q);
    const finalResult = hasNonExact ? result : [];
    this.cacheSuggestions(this.groupSuggestionCache, q, finalResult);
    return finalResult;
  }

  // Return top 5 closest object value matches to the current input
  getObjectSuggestions(query: string | undefined): string[] {
    const q = this.normalizeSuggestionQuery(query);
    if (!q) return [];
    const cached = this.objectSuggestionCache.get(q);
    if (cached) return cached;
    const starts: string[] = [];
    const contains: string[] = [];
    for (let i = 0; i < this.allObjectValues.length; i++) {
      const value = this.allObjectValues[i];
      const v = this.allObjectValuesLower[i];
      if (v.startsWith(q)) {
        starts.push(value);
      } else if (v.includes(q)) {
        contains.push(value);
      }
      if (starts.length >= 5) break;
    }
    const combined = starts.concat(contains);
    const seen = new Set<string>();
    const result: string[] = [];
    for (const s of combined) {
      if (!seen.has(s)) {
        seen.add(s);
        result.push(s);
        if (result.length >= 5) break;
      }
    }
    const hasNonExact = result.some(s => s.toLowerCase() !== q);
    const finalResult = hasNonExact ? result : [];
    this.cacheSuggestions(this.objectSuggestionCache, q, finalResult);
    return finalResult;
  }

  // Whether current input value exactly matches a known group name (case-insensitive)
  isExactGroupMatch(query: string | undefined): boolean {
    const q = this.normalizeSuggestionQuery(query);
    if (!q) return false;
    return this.allGroupNamesSet.has(q);
  }

  private normalizeSuggestionQuery(query: string | undefined): string {
    return (query || '').trim().toLowerCase();
  }

  private cacheSuggestions(cache: Map<string, string[]>, key: string, value: string[]): void {
    if (cache.size >= this.suggestionCacheLimit) {
      cache.clear();
    }
    cache.set(key, value);
  }

  // Focus handling for suggestion display
  setInputFocus(obj: any, focused: boolean) {
    if (!obj) return;
    if (focused) {
      this.focusedInputs.add(obj);
    } else {
      this.focusedInputs.delete(obj);
    }
  }

  isFocused(obj: any): boolean {
    return this.focusedInputs.has(obj);
  }

  // Apply a suggestion to a group member input and trigger change handling
  applyGroupSuggestion(target: any, value: string, inputEl?: HTMLInputElement, list?: any[]) {
    if (!target) return;
    target.memberName = value;
    target.memberType = 'group';
    // Reuse existing input change pipeline
    this.onInputChange(target, list, 'group');
    // Keep focus on the input and move cursor to end
    if (inputEl) {
      // Small timeout to allow Angular to update the bound value
      setTimeout(() => {
        try {
          this.setInputFocus(target, true);
          inputEl.focus();
          const len = (target.memberName || '').toString().length;
          inputEl.setSelectionRange?.(len, len);
        } catch {}
      }, 0);
    }
  }

  // Apply a suggestion to an object member input and trigger change handling
  applyObjectSuggestion(target: any, value: string, inputEl?: HTMLInputElement, list?: any[]) {
    if (!target) return;
    target.memberName = value;
    target.memberType = this.matchInputType(value) || '';
    this.onInputChange(target, list, target.memberType);
    if (inputEl) {
      // Small timeout to allow Angular to update the bound value
      setTimeout(() => {
        try {
          this.setInputFocus(target, true);
          inputEl.focus();
          const len = (target.memberName || '').toString().length;
          inputEl.setSelectionRange?.(len, len);
        } catch { }
      }, 0);
    }
  }

  private ensureOneTrailingBlank(list?: any[], typeHint: string = '') {
    if (!Array.isArray(list)) return;
    // Remove any blanks except the last one
    for (let i = list.length - 2; i >= 0; i--) {
      const item = list[i];
      const v = (item?.memberName ?? '').toString().trim();
      if (v === '') {
        this.clearIssue(item);
        list.splice(i, 1);
      }
    }
    const last = list[list.length - 1];
    const lastBlank = !last || (typeof last.memberName === 'string' ? last.memberName.trim() === '' : last.memberName === '' || last.memberName === undefined);
    if (!last || !lastBlank) {
      list.push({
        groupName: this.selectedEditNode?.groupName || '',
        isRoot: 0,
        memberName: '',
        memberTarget: null,
        memberType: typeHint === 'group' ? 'group' : '',
        serialNumber: this.selectedInventory?.serialNumber || '',
        requestFocus: false,
        _isNewRow: true,
        uid: `input-${Date.now()}-${Math.random().toString(36).slice(2)}`
      });
    }
  }

  // Try to focus the input element bound to the given item using its uid
  private focusItemInput(item: any) {
    if (!item || !item.uid) return;
    const selector = `[data-uid='${item.uid}']`;
    // Defer to allow DOM to update after list mutation
    setTimeout(() => {
      const el = document.querySelector(selector) as HTMLInputElement | null;
      if (el) {
        try {
          el.focus();
          const len = (item.memberName || '').toString().length;
          el.setSelectionRange?.(len, len);
        } catch {}
      }
    }, 0);
  }

  updateChildrenParentName(group: any) {
    if (!group.children) return;
    for (const child of group.children) {
      child.parent = group.memberName;
      if (child.memberType === 'group') {
        this.updateChildrenParentName(child);
      }
    }
  }

  // Invalid marker used by template
  isInvalid(obj: any): boolean {
    return this.isInputError(obj);
  }

  isDraftGroupNameMissing(obj: any): boolean {
    return !!(obj && (obj as any).isRootDraft === true && ((obj?.groupName ?? '').toString().trim() === ''));
  }

  // Apply pending "View Changes" entries for the active group into the edit form
  private generateSecureUid(): string {
    const prefix = 'input-' + Date.now() + '-';
    try {
      const cryptoObj: Crypto | undefined =
        (typeof window !== 'undefined' && (window.crypto || (window as any).msCrypto)) ||
        (typeof self !== 'undefined' && (self as any).crypto);

      if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
        const bytes = new Uint32Array(4);
        cryptoObj.getRandomValues(bytes);
        const randomPart = Array.from(bytes)
          .map(b => b.toString(36))
          .join('');
        return prefix + randomPart;
      }
    } catch {
      // Fallback to Math.random() if crypto is unavailable; still include timestamp for uniqueness.
    }

    // Non-crypto fallback (legacy environments).
    const fallbackRandom = Math.random().toString(36).slice(2);
    return prefix + fallbackRandom;
  }

  private applyPendingChangesFromViewChanges(groupName: string) {
    if (!this.selectedEditNode) return;
    const key = (groupName || '').trim().toLowerCase();
    if (!key) return;

    // Always prefer UserService as the source-of-truth. `allFilteredChanges` is a derived view
    // that can lag behind BehaviorSubject updates during remove→navigate flows and re-apply
    // a summary line that the user just removed.
    const pendingSource = this._user.getFilteredChanges?.() ?? [];

    const pendingGroup = pendingSource.find(g => (g.groupName || '').trim().toLowerCase() === key);
    if (!pendingGroup || !Array.isArray(pendingGroup.changes) || pendingGroup.changes.length === 0) {
      return;
    }

    const ensureArray = (isGroup: boolean) => {
      const prop = isGroup ? 'memberGroups' : 'memberObjects';
      if (!Array.isArray((this.selectedEditNode as any)[prop])) {
        (this.selectedEditNode as any)[prop] = [];
      }
      return (this.selectedEditNode as any)[prop] as any[];
    };

    for (const change of pendingGroup.changes) {
      const summary = (change?.summary || '').trim();
      const parsed = summary.match(/^(Add|Remove)\s+(\w+):\s+(.+)$/i);
      if (!parsed) continue;

      const [, actionRaw, typeRaw, nameRaw] = parsed;
      const action = (actionRaw || '').toLowerCase();
      const memberType = (typeRaw || 'unknown').toLowerCase();
      const memberName = (nameRaw || '').trim();
      if (!memberName) continue;

      const isGroup = memberType === 'group';
      const targetArray = ensureArray(isGroup);
      const existing = targetArray.find((m: any) => (m.memberName || '').trim().toLowerCase() === memberName.toLowerCase());

      if (action === 'add') {
        if (existing) {
          existing.memberType = memberType;
          this.removedMembers.delete(existing);
        } else {
          targetArray.push({
            memberName,
            memberType,
            serialNumber: this.selectedInventory?.serialNumber || '',
            requestFocus: false,
            uid: this.generateSecureUid()
          });
        }
      } else if (action === 'remove') {
        const target = existing || (() => {
          const placeholder = {
            memberName,
            memberType,
            serialNumber: this.selectedInventory?.serialNumber || '',
            requestFocus: false,
            uid: this.generateSecureUid()
          };
          targetArray.push(placeholder);
          return placeholder;
        })();
        this.removedMembers.add(target);
      }
    }

    if (key) {
      this.editedNodesByGroup.set(key, JSON.parse(JSON.stringify(this.selectedEditNode)));
    }
  }

  private getExistingNonMemberChangesForGroup(groupName: string): Array<{ summary: string; meta?: string }> {
    const name = (groupName || '').toString().trim();
    if (!name) return [];
    const key = this.normalizeGroupKey(name);
    if (!key) return [];

    const current = this._user.getFilteredChanges?.() ?? [];
    if (!Array.isArray(current) || current.length === 0) return [];

    const entry = current.find((g: any) => this.normalizeGroupKey(g?.groupName) === key);
    const changes = Array.isArray(entry?.changes) ? entry.changes : [];

    // Keep non-member diff lines so updateEditDiffs doesn't wipe them.
    // Member diffs are of the form "Add type: value" / "Remove type: value".
    return changes.filter((c: any) => {
      const s = (c?.summary ?? '').toString().trim();
      if (!s) return false;
      if (/^(add|remove)\s+\w+\s*:\s+/i.test(s)) return false;
      // Rename subgroup lines are produced by updateEditDiffs when groupName changes.
      if (/^rename\s+subgroup\s*:/i.test(s)) return false;
      return true;
    }).map((c: any) => ({ summary: (c.summary ?? '').toString(), meta: c.meta }));
  }

  private dedupeChangeList(items: Array<{ summary: string; meta?: string }>): Array<{ summary: string; meta?: string }> {
    if (!Array.isArray(items) || items.length === 0) return [];
    const seen = new Set<string>();
    const out: Array<{ summary: string; meta?: string }> = [];
    for (const c of items) {
      if (!c || !c.summary) continue;
      const key = `${(c.summary ?? '').toString().trim()}|${(c.meta ?? '').toString().trim()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }

  /**
   * Compute diffs between the original snapshot and the current selectedEditNode.
   * Classifies changes as add/remove/update (update => remove old + add new).
   * Keeps a concise summary in filteredChanges for the side panel.
   */
  private updateEditDiffs(skipValidation: boolean = false) {
    // Always keep validation in sync with the current edit lists unless already done.
    if (!skipValidation && this.selectedEditNode) {
      this.validateMemberList((this.selectedEditNode as any).memberGroups, 'group');
      this.validateMemberList((this.selectedEditNode as any).memberObjects, '');
    }

    // Do not publish diffs while in the Create New Group draft flow
    if (this.selectedEditNode && (this.selectedEditNode as any).isRootDraft === true) {
      return;
    }
    if (!this.selectedEditNode || !this.originalEditNodeSnapshot) {
      // Aggregation removed; rely on filteredChanges$ subscription
      return;
    }

    const groupName = this.selectedEditNode.groupName || '(unnamed)';

    const before = this.normalizeNodeForDiff(this.originalEditNodeSnapshot);
    const after = this.normalizeNodeForDiff(this.selectedEditNode);

    // If this group is staged as newly created in the current CR (Create group: X), the "baseline"
    // snapshot we have in the editor may already include the added members (because we inserted the
    // node into the tree). In that case, diffs would be empty and we'd wipe out the saved Add lines.
    // Treat created groups as having an empty baseline for member diffs.
    try {
      const groupNameKey = this.normalizeGroupKey(after.groupName || this.selectedEditNode.groupName || '');
      const nonMember = this.getExistingNonMemberChangesForGroup(after.groupName || this.selectedEditNode.groupName || '');
      const hasCreateForThisGroup = nonMember.some((c: any) => {
        const s = (c?.summary ?? '').toString().trim();
        const m = s.match(/^Create\s+group:\s+(.+)$/i);
        if (!m) return false;
        const createdName = (m[1] || '').toString().trim();
        return this.normalizeGroupKey(createdName) === groupNameKey;
      });
      if (hasCreateForThisGroup) {
        before.members = [];
        before.groupName = after.groupName;
      }
    } catch {}

    const beforeSet = new Map<string, { value: string; type: string }>();
    before.members.forEach(m => beforeSet.set(this.memberKey(m), m));

    const afterSet = new Map<string, { value: string; type: string }>();
    after.members.forEach(m => afterSet.set(this.memberKey(m), m));

    const changes: Array<{ summary: string; meta?: string }> = [];

    // Detect removals and additions only when not handling a top-level rename
    const isTopLevelRename = (before.groupName || '') !== (after.groupName || '') && this.isTopLevelSelectedEditNode();
    if (!isTopLevelRename) {
      // Handle explicitly marked removed members first
      this.removedMembers.forEach(member => {
        const memberName = (member.memberName || '').trim();
        const memberType = (member.memberType || '').toLowerCase();
        if (memberName) {
          const normalized = this.normalizeValueForChangeSummary(memberType || 'unknown', memberName);
          changes.push({ summary: `Remove ${memberType || 'unknown'}: ${normalized}` });
        }
      });
      
      // Detect removals and updates (value changed for same type vs. previous)
      for (const [key, b] of beforeSet.entries()) {
        if (!afterSet.has(key)) {
          // Check if this removal is already tracked in removedMembers
          const alreadyTracked = Array.from(this.removedMembers).some(member => {
            const mn = (member.memberName || '').trim();
            const mt = (member.memberType || '').toLowerCase();
            return mn === b.value && mt === b.type;
          });
          
          if (!alreadyTracked) {
            // Removed or updated: check if type matches but value differs
            const sameTypeNew = after.members.find(m => m.type === b.type && m.value === b.value);
            if (!sameTypeNew) {
              changes.push({ summary: `Remove ${b.type}: ${b.value}` });
            }
          }
        }
      }

      // Detect additions
      for (const [key, a] of afterSet.entries()) {
        if (!beforeSet.has(key)) {
          const trimmed = (a.value || '').trim();
          if (trimmed) {
            const normalized = this.normalizeValueForChangeSummary(a.type || 'unknown', trimmed);
            changes.push({ summary: `Add ${a.type || 'unknown'}: ${normalized}`, meta: `Under ${after.groupName || 'group'}` });
          }
        }
      }
    }

    // Optional: groupName change
    if ((before.groupName || '') !== (after.groupName || '')) {
      const beforeRaw = (before.groupName || '').toString();
      const afterRaw = (after.groupName || '').toString();
      const bgn = beforeRaw || '—';
      const agn = afterRaw || '—';
      // Top-level groups cannot be renamed; represent as delete + recreate
      if (this.isTopLevelSelectedEditNode()) {
        // Represent as delete old group, create new group, then add all children
        // If the previous group name was blank, there is nothing to delete
        if (beforeRaw.trim() !== '') {
          changes.unshift({ summary: `Delete group: ${bgn}` });
        }
        changes.unshift({ summary: `Create group: ${agn}` });
        // Explicitly list additions for each child under the new group
        for (const child of after.members) {
          const v = (child.value || '').trim();
          if (!v) continue;
          const t = (child.type || 'unknown').trim();
          const normalized = this.normalizeValueForChangeSummary(t, v);
          changes.push({ summary: `Add ${t}: ${normalized}`, meta: `Under ${agn}` });
        }
      } else {
        changes.unshift({ summary: `Rename subgroup: ${bgn} → ${agn}` });
      }
    }
    
    // Update the observable with changes for this group
    if (this.selectedEditNodeIndex !== null && this.selectedEditNode) {
      const groupName = this.selectedEditNode.groupName || '(unnamed group)';
      // Preserve any existing non-member lines (e.g., Create/Delete group) so editing/removal
      // doesn't collapse the View Changes pane to only the last recomputed member diff.
      const extras = this.getExistingNonMemberChangesForGroup(groupName);
      const merged = this.dedupeChangeList([...extras, ...changes]);
      this._user.updateFilteredChanges(this.selectedEditNodeIndex, groupName, merged);
    }
    // Persist changes for active group (for restoring inputs)
    const groupKey = this.activeGroupKey || this.normalizeGroupKey(this.selectedEditNode.groupName || this.selectedEditNode.value || '');
    if (groupKey) {
      const display = (this.selectedEditNode.groupName || this.selectedEditNode.value || '').toString();
      this.pendingChangesByGroup.set(groupKey, changes.map(c => ({ ...c, meta: c.meta ? c.meta : `Group • ${display}` })));
      // Persist the current edited node state so reopening the group restores inputs
      this.editedNodesByGroup.set(groupKey, JSON.parse(JSON.stringify(this.selectedEditNode)));
    }
    // Aggregation removed; View Changes pane updates via filteredChanges$
  }

  private memberKey(m: { value: string; type: string }): string {
    const v = (m?.value || '').trim();
    const t = (m?.type || '').trim();
    return `${t}::${v}`;
  }

  private normalizeNodeForDiff(node: any): { groupName: string; members: Array<{ value: string; type: string }> } {
    const groupName = node?.groupName || node?.value || '';
    const members: Array<{ value: string; type: string }> = [];

    const pushValid = (item: any) => {
      if (!item) return;
      const value = (item.value ?? item.memberName ?? '').toString();
      const type = (item.type ?? item.memberType ?? '').toString().toLowerCase();
      const trimmed = value.trim();
      if (!trimmed) return;

      // Enforce: only existing groups can be staged as group members.
      if (type === 'group' && !this.isExistingGroupName(trimmed)) {
        return;
      }

      members.push({ value: trimmed, type });
    };

    const hasStructured = Array.isArray(node?.memberGroups) || Array.isArray(node?.memberObjects);
    if (Array.isArray(node?.memberGroups)) {
      node.memberGroups.forEach(pushValid);
    }
    if (Array.isArray(node?.memberObjects)) {
      node.memberObjects.forEach(pushValid);
    }

    // Use flat 'members' only when structured arrays are not present to avoid duplicates
    if (!hasStructured && Array.isArray(node?.members)) {
      node.members.forEach(pushValid);
    }

    return { groupName, members };
  }

  // Aggregate all group changes into filteredChanges, optionally applying active filter
  // Local aggregation removed; View Changes pane now derives from filteredChanges$ only.

  // Determine if the given node is currently being edited (by group identity)
  isEditingGroup(node: any): boolean {
    if (!node || !this.selectedEditNode) return false;
    const a = (this.selectedEditNode.groupName || this.selectedEditNode.value || '').toString();
    const b = (node.groupName || node.value || '').toString();
    return a === b;
  }
  
  // Determine whether the currently edited node is a top-level group
  private isTopLevelSelectedEditNode(): boolean {
    // Top-level groups have no parent assigned; drafts are considered top-level
    const n = this.selectedEditNode;
    const hasParent = !!(n && (n.parent !== undefined && n.parent !== null && `${n.parent}`.trim() !== ''));
    return !hasParent;
  }

  /**
   * Load active changes for the dropdown
   */
  async refreshActiveChanges(): Promise<void> {
    try {
      const changes = await firstValueFrom(this._change.getActiveChanges());
      this.activeChanges = Array.isArray(changes) ? changes : [];
      this.currentChange = this.activeChanges.find(c => c.crNumber === this.currentCrNumber) || null;
      this.applyCrSelectionFromUrl();
    } catch (err) {
      console.error('Error fetching active changes:', err);
    }
  }

  onChangeSelected(crNumberIn?: string, options?: { suppressConfirm?: boolean }): void {
    // Clear any previous "selection required" prompt once user interacts with Change Control.
    this.changeSelectionError = '';
    const suppressConfirm = options?.suppressConfirm === true;
    const requestedCr = (crNumberIn ?? this.currentCrNumber) as any;
    const previousCr = (this.lastSelectedCrNumber ?? '') as any;

    // If nothing requested, no-op.
    if (requestedCr === undefined || requestedCr === null || `${requestedCr}`.trim() === '') {
      return;
    }

    const isSwitching = `${requestedCr ?? ''}` !== `${previousCr ?? ''}`;
    if (isSwitching) {
      const shouldConfirm = this.hasAnyPendingEdits();
      if (!suppressConfirm && shouldConfirm) {
        const ok = confirm('Changing the selected Change Request will clear any unsaved edits and reload the saved Change Control changes. Proceed?');
        if (!ok) {
          // Restore the UI selection.
          this.currentCrNumber = previousCr ?? '';
          return;
        }
      }

      this.currentCrNumber = requestedCr;
      this.currentChange = null;
      // Change Control is the master: always reset local pending edits on CR switch.
      this.resetAllChangeControlEdits();
      this.lastSelectedCrNumber = `${requestedCr}`;
    } else {
      // Ensure dropdown-selected value is reflected.
      this.currentCrNumber = requestedCr;
    }

    if (this.currentCrNumber === "CREATE_NEW") {
      this.showNewCrInput = true;
      this.currentChange = null;
      this.viewChangesLoading = false;
      this.lastSelectedCrNumber = `${this.currentCrNumber}`;
    } else {
      this.showNewCrInput = false;
      let selectedChange = this.activeChanges.find(c => c.crNumber === this.currentCrNumber) || null;

      if (selectedChange) {
        this.currentChange = selectedChange;

        // Show a dedicated spinner in the View Changes pane while switching CRs.
        // We intentionally yield to the next tick so Angular can render the spinner
        // before the (potentially heavy) cliConfig load/derived computations run.
        const seq = ++this.viewChangesLoadSeq;
        const startedAt = Date.now();
        this.viewChangesLoading = true;

        const minVisibleMs = 250;
        window.setTimeout(() => {
          // If the user switched CRs again before this tick, ignore.
          if (seq !== this.viewChangesLoadSeq) return;

          try {
            const cliConfig = this.currentChange?.cliConfig;
            // Clear again defensively (state was already reset on switch).
            this._user.clearFilteredChanges();

            if (cliConfig) {
              // Load the parsed cliConfig into the observable
              this._user.loadFilteredChanges(cliConfig);
            }

            // Align local group indices with the loaded changes so edits/removals update the
            // correct slots (prevents stale View Changes after save→delete sequences).
            this.syncGroupIndexMapFromUserFilteredChanges({ reset: true });
          } catch (err) {
            console.error('Error loading cliConfig from selected change:', err);
          } finally {
            const elapsed = Date.now() - startedAt;
            const remaining = Math.max(0, minVisibleMs - elapsed);
            window.setTimeout(() => {
              if (seq === this.viewChangesLoadSeq) {
                this.viewChangesLoading = false;
              }
            }, remaining);
          }
        }, 0);

        this.lastSelectedCrNumber = `${this.currentCrNumber}`;
      } else {
        // No matching CR found; ensure we don't get stuck showing a spinner.
        this.viewChangesLoading = false;
        this.lastSelectedCrNumber = `${this.currentCrNumber}`;
      }
    }
  }

  jumpToChange(crNumber: string) {
    if (!crNumber || crNumber === 'CREATE_NEW') {
      return;
    }
    const urlTree = this.router.createUrlTree(['/change'], { queryParams: { crNumber } });
    const url = this.location.prepareExternalUrl(this.router.serializeUrl(urlTree));
    window.open(url, '_blank');
  }
}